import { Box, Card, Input, InputAdornment, Menu, CircularProgress, Link, Button, Typography, Stack, MenuItem, IconButton, Chip, Tooltip, Alert, TextField , Autocomplete, Popover, FormControl, FormControlLabel, Checkbox, Divider, FormLabel, Radio, RadioGroup } from "@mui/material";
import { APIProvider, Map as GoogleMap, AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { Cluster, ClusterStats, Marker, MarkerClusterer, MarkerUtils, Renderer, SuperClusterOptions } from '@googlemaps/markerclusterer';
import React, { useCallback, useEffect, useMemo, useState, useRef, memo } from "react";
import { useAuthContext } from "src/auth/hooks";
import Iconify from "src/components/iconify";
import SimpleDialog from "src/components/simple-dialog";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import { EldDriverStatusLabel } from "src/components/eld-driver-status-label";
import useModelData, { replaceById } from "src/hooks/use-model-data";
import { Device, DeviceEldDriver, DeviceHistoryLineWithCityName, DeviceWithMapInfo, EldDriverLogGroup, EldEngineImmobilizer, EldLandmark, EldVehicle, EldVehicleStats } from "src/models";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { formatDatetimeForPreview, formatTimeAsHourMin } from "src/utils/date-utils";
import { GOOGLE_MAPS_API_KEY, HOST_API } from "src/config-global";
import { Colors } from "src/theme/palette";
import TruxFormField from "src/components/inputs/trux-form-field";
import { enqueueSnackbar } from "notistack";
import { useVirtualizer } from '@tanstack/react-virtual';
import TruxSimpleDateField from "src/components/inputs/trux-simple-date-field";
import { usePrevious } from "src/hooks/use-previous";
import { useLocalStorage } from "src/hooks/use-local-storage";
import dayjs from "dayjs";
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import TruxSimpleDateTimeField from "src/components/inputs/trux-simple-date-time-field";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { DateTime } from "luxon";
import { DatePicker } from "@mui/x-date-pickers";
import { overSpeed, getDeviceDrivers } from 'src/utils/device-utils';
import { STORAGE_KEY } from "src/auth/context/auth-provider";
import { useResponsive } from "src/hooks/use-responsive";
import { VideoJS } from "./video-js";
import VehicleInfo from "./vehicle-info";

// Map configuration
export const MAP_TYPES = [
  { key: 'default', label: 'Default' },
  { key: 'satellite', label: 'Satellite' },
  { key: 'terrain', label: 'Terrain' }
] as const;

export const MAP_OVERLAYS = [
  { key: 'traffic', label: 'Traffic' },
  { key: 'landmarks', label: 'Landmarks' },
  { key: 'geofences', label: 'Geofences' },
  { key: 'weighStation', label: 'Weigh Stations' },
] as const;

type PageModel = {
  devices?: DeviceWithMapInfo[],
  eldEngineImmobilizers?: EldEngineImmobilizer[],
  eldLandmarks?: EldLandmark[],
  eldVehicles?: EldVehicle[],
  eldConnectedVehicleUnitNumbers: string[],
  eldDriverLogGroups?: EldDriverLogGroup[],
  deviceEldDrivers?: DeviceEldDriver[],
}

type DeviceHistoryLineWithCityNameGroup = DeviceHistoryLineWithCityName & {
  lines: DeviceHistoryLineWithCityName[]
}

export type MarkerData = {
  device: DeviceWithMapInfo;
  position: google.maps.LatLngLiteral;
}

export default function Page() {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <PageContent />
    </APIProvider>
  );
}

const useSockets = true;

function useSocket(endPoint: string, onMessageReceived: (data: any) => void) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldConnectRef = useRef(true);

  const onReceivedRef = useRef(onMessageReceived);
  useEffect(() => {
    if (!useSockets) {
      return
    }

    onReceivedRef.current = onMessageReceived;
  }, [onMessageReceived])

  const connect = useCallback(() => {
    if (!useSockets) {
      return
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('warning: Already connected');
      return;
    }

    console.log('Attempting to connect to:', endPoint);

    try {
      wsRef.current = new WebSocket(HOST_API + endPoint);

      wsRef.current.onopen = (event) => {
        console.log('system: Connected to server');
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // console.log(`${data.type} [${data.timestamp}]:`, data.data, event);
          onReceivedRef?.current?.(data);
        } catch (e) {
          console.log('raw:', event.data);
          onReceivedRef?.current?.(event.data);
        }
      };

      wsRef.current.onerror = (error) => {
        console.log('error: WebSocket error occurred');
        console.error('WebSocket error:', error);
      };

      wsRef.current.onclose = (event) => {
        console.log(`system: Connection closed (Code: ${event.code}, Reason: ${event.reason || 'None'})`);
        wsRef.current = null;

        // Auto-reconnect after 2 seconds if component is still mounted
        if (shouldConnectRef.current) {
          console.log('Reconnecting in 2 seconds...');
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 2000);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      // Retry connection after 2 seconds
      if (shouldConnectRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 2000);
      }
    }
  }, [endPoint]);

  useEffect(() => {
    if (!useSockets) {
      return () => {}
    }
    shouldConnectRef.current = true;
    connect();

    return () => {
      shouldConnectRef.current = false;

      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Close WebSocket connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return {
    send: (data: any) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(data));
        console.log('Sent:', data);
      } else {
        console.log('error: Cannot send, WebSocket is not connected');
      }
    },
    disconnect: () => {
      shouldConnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    }
  };
}

// endpoints.devices.realtime + '?token=' + encodeURIComponent(localStorage.getItem(STORAGE_KEY) || ""), processDevicesUpdateResponse

function PageContent() {
  const dataLoader = useCallback(() => ({
    devices: axios.get(endpoints.devices.findAll).then(response => response.data),
    eldEngineImmobilizers: axios.get(endpoints.eldEngineImmobilizer.findAll).then(response => response.data),
    eldLandmarks: axios.get(endpoints.eldLandmarks.findAll).then(response => response.data),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
    eldConnectedVehicleUnitNumbers: [],
    eldDriverLogGroups: axios.get(endpoints.eldDriverLogGroups.findAll).then(response => response.data),
    deviceEldDrivers: axios.get(endpoints.deviceEldDriver.findAll).then(response => response.data),
  }), []);

  const {
    data,
    updateData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const deviceEldDrivers = useMemo(() => data?.deviceEldDrivers || [], [data?.deviceEldDrivers]);

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const [deviceHistory, setDeviceHistory] = useState<DeviceHistoryLineWithCityNameGroup[]>([]);
  const [deviceForHistory, setDeviceForHistory] = useState<DeviceWithMapInfo>();
  const [dateForHistory, setDateForHistory] = useState(DateTime.now().setZone(timezone || "America/Chicago").toFormat("yyyy-MM-dd"));

  const processDevicesUpdateResponseSocket = useCallback(async (partialDevice: Record<string, any>) => {
    let changeFound = false;
    const devices = (data?.devices || []);

    devices.forEach(device => {
      if (device.id !== partialDevice.id) {
        return;
      }
      const deviceWithLocation = {...device};

      Object.keys(partialDevice).forEach(key => {
        (deviceWithLocation as any)[key] = partialDevice[key];
      });

      if (device.lastPing !== deviceWithLocation.lastPing
        || device.velocity !== deviceWithLocation.velocity
        || device.direction !== deviceWithLocation.direction
        || device.battery !== deviceWithLocation.battery
        || device.lastPosLat !== deviceWithLocation.lastPosLat
        || device.lastPosLong !== deviceWithLocation.lastPosLong
        || device.active !== deviceWithLocation.active
        || device.organizationId !== deviceWithLocation.organizationId
        || device.lastOdometer !== deviceWithLocation.lastOdometer
        || device.origin !== deviceWithLocation.origin
        || device.deviceType !== deviceWithLocation.deviceType
        || JSON.stringify(device.camera) !== JSON.stringify(deviceWithLocation.camera)
        || device.location !== deviceWithLocation.location) {

        device.lastPing = deviceWithLocation.lastPing;
        device.lastPosLat = deviceWithLocation.lastPosLat;
        device.lastPosLong = deviceWithLocation.lastPosLong;
        device.velocity = deviceWithLocation.velocity;
        device.direction = deviceWithLocation.direction;
        device.battery = deviceWithLocation.battery;
        device.active = deviceWithLocation.active;
        device.organizationId = deviceWithLocation.organizationId;
        device.lastOdometer = deviceWithLocation.lastOdometer;
        device.origin = deviceWithLocation.origin;
        device.deviceType = deviceWithLocation.deviceType;
        device.camera = deviceWithLocation.camera;
        device.location = deviceWithLocation.location;

        changeFound = true;


        // Insert into location history if this event belongs to the date that is being viewed
        const currentDeviceForHistory = deviceForHistory;
        const currentDateForHistory = dateForHistory;

        if (
          currentDeviceForHistory &&
          deviceWithLocation.deviceUuid === currentDeviceForHistory.deviceUuid &&
          currentDateForHistory &&
          deviceWithLocation.lastPosLat &&
          deviceWithLocation.lastPosLong &&
          deviceWithLocation.lastPing &&
          (partialDevice.lastPing ||
            partialDevice.lastPosLat ||
            partialDevice.lastPosLong ||
            partialDevice.location ||
            partialDevice.lastOdometer ||
            partialDevice.battery ||
            partialDevice.direction ||
            partialDevice.velocity)
        ) {
          const dateOfUpdate = DateTime.fromISO(deviceWithLocation.lastPing)
            .setZone(timezone || "America/Chicago")
            .toFormat("yyyy-MM-dd");

          if (dateOfUpdate === currentDateForHistory) {
            setDeviceHistory((prevHistory: DeviceHistoryLineWithCityNameGroup[]) => {
              if (!prevHistory || prevHistory.length === 0) {
                // no prior history
                return [{
                  recordedAt: deviceWithLocation.lastPing || '',
                  posLat: deviceWithLocation.lastPosLat,
                  posLong: deviceWithLocation.lastPosLong,
                  cityName: deviceWithLocation.location || '',
                  odometer: deviceWithLocation.lastOdometer,
                  battery: deviceWithLocation.battery,
                  direction: deviceWithLocation.direction,
                  velocity: deviceWithLocation.velocity,
                  lines: []
                }];
              }

              // can we group with latest location?
              const updatedHistory = [...prevHistory];
              const lastEntry = updatedHistory[updatedHistory.length - 1];

              if (isTheSame(lastEntry.posLat, lastEntry.posLong, deviceWithLocation.lastPosLat, deviceWithLocation.lastPosLong)) {
                // Add to existing group's lines array
                lastEntry.lines.push({
                  recordedAt: deviceWithLocation.lastPing || '',
                  posLat: deviceWithLocation.lastPosLat,
                  posLong: deviceWithLocation.lastPosLong,
                  cityName: deviceWithLocation.location || '',
                  odometer: deviceWithLocation.lastOdometer,
                  battery: deviceWithLocation.battery,
                  direction: deviceWithLocation.direction,
                  velocity: deviceWithLocation.velocity,
                });
              } else {
                // Create new location group
                updatedHistory.push({
                  recordedAt: deviceWithLocation.lastPing || '',
                  posLat: deviceWithLocation.lastPosLat,
                  posLong: deviceWithLocation.lastPosLong,
                  cityName: deviceWithLocation.location || '',
                  odometer: deviceWithLocation.lastOdometer,
                  battery: deviceWithLocation.battery,
                  direction: deviceWithLocation.direction,
                  velocity: deviceWithLocation.velocity,
                  lines: []
                });
              }

              return updatedHistory;
            });
          }
        }
      }
    })

    if (changeFound) {
      updateData("devices")([...devices]);
    }
  }, [data?.devices, updateData, timezone, deviceForHistory, dateForHistory]);


  const processDevicesUpdateResponse = useCallback((response: any) => {
    let changeFound = false;
    const devices = (data?.devices || []);
    devices.forEach(device => {
      (response.data as DeviceWithMapInfo[]).forEach((resDevice) => {
        if (device.deviceUuid === resDevice.deviceUuid) {
          if (device.lastPing !== resDevice.lastPing
            || device.velocity !== resDevice.velocity
            || device.direction !== resDevice.direction
            || device.battery !== resDevice.battery
            || device.lastPosLat !== resDevice.lastPosLat
            || device.lastPosLong !== resDevice.lastPosLong
            || device.origin !== resDevice.origin
            || device.lastOdometer !== resDevice.lastOdometer
            || device.active !== resDevice.active
            || device.organizationId !== resDevice.organizationId
            || device.deviceType !== resDevice.deviceType
            || JSON.stringify(device.camera) !== JSON.stringify(resDevice.camera)
            || device.location !== resDevice.location) {

            device.lastPing = resDevice.lastPing;
            device.lastPosLat = resDevice.lastPosLat;
            device.lastPosLong = resDevice.lastPosLong;
            device.velocity = resDevice.velocity;
            device.direction = resDevice.direction;
            device.battery = resDevice.battery;
            device.origin = resDevice.origin;
            device.lastOdometer = resDevice.lastOdometer;
            device.active = resDevice.active;
            device.organizationId = resDevice.organizationId;
            device.deviceType = resDevice.deviceType;
            device.camera = resDevice.camera;
            device.location = resDevice.location;
            changeFound = true;
          }
        }
      })
    })
    if (changeFound) {
      updateData("devices")([...devices]);
    }
  }, [data?.devices, updateData]);

  useEffect(() => {
    if (useSockets) {
      return () => {};
    }
    const interval = setInterval(() => {
      axios.get(endpoints.devices.findAll).then(processDevicesUpdateResponse)
    }, 10_000)
    return () => {
      clearInterval(interval);
    }
  }, [processDevicesUpdateResponse])


  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(endpoints.deviceEldDriver.findAll).then(response => updateData("deviceEldDrivers")(response.data))
    }, 20_000)
    return () => {
      clearInterval(interval);
    }
  }, [updateData])

  useSocket(`${endpoints.devices.realtime}?token=${encodeURIComponent(localStorage.getItem(STORAGE_KEY) || "")}`, processDevicesUpdateResponseSocket)

  const [selectedUuid, setSelectedUuid] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedType, setSelectedType] = useState("0");
  const [tileFilters, setTileFilters] = useState<Set<string>>(new Set());
  const [selectedMarker, setSelectedMarker] = useState(-1);
  const [deviceWithDrivers, setDeviceWithDrivers] = useState<DeviceWithMapInfo>();
  const [selectedView, setSelectedView] = useState<"vehicles" | "drivers" | "deviceWithDrivers">("vehicles");
  const [previousView, setPreviousView] = useState<"vehicles" | "drivers">("vehicles");
  const [mapOptionsOpen, setMapOptionsOpen] = useState<HTMLElement | null>(null);

  const { state: mapSettings, update: updateMapSettings } = useLocalStorage('trux-map-settings', {
    mapType: 'default',
    selectedOptions: []
  });

  const mdUp = useResponsive('up', 'md');

  const [viewingMode, setViewingMode] = useState<"sidebar" | "history">(mdUp ? "sidebar" : "history");

  useEffect(() => {
    if (!mdUp) {
      setViewingMode("history");
    }
  }, [mdUp]);

  const [shareLocationDialogOpen, setShareLocationDialogOpen] = useState<boolean>(false);
  const [selectedVehicles, setSelectedVehicles] = useState<DeviceWithMapInfo[]>([]);
  const [expirationDate, setExpirationDate] = useState<string>(DateTime.now().plus({ days: 7 }).toISO());

  const handleShareLocationClick = useCallback((vehicle?: DeviceWithMapInfo) => {
    if (vehicle) {
      setSelectedVehicles([vehicle]);
    } else {
      setSelectedVehicles([]);
    }
    const defaultExpiration = DateTime.now().plus({ days: 7 }).toISO();
    setExpirationDate(defaultExpiration);
    setShareLocationDialogOpen(true);
  }, []);

  const handleTruckMarkerClick = useCallback((device: DeviceWithMapInfo, drivers: DeviceEldDriver[]) => {
    if (selectedView === "vehicles" || selectedView === "drivers") {
      setPreviousView(selectedView);
    }
    setDeviceWithDrivers(device);
    setSelectedView("deviceWithDrivers");
    setViewingMode("sidebar");
    setSelectedUuid(device.deviceUuid);
  }, [selectedView]);

  const eldEngineImmobilizerByDeviceId = useMemo(() => {
    const map = new Map<number, EldEngineImmobilizer>();
    data?.eldEngineImmobilizers?.forEach(eldEngineImmobilizer => map.set(eldEngineImmobilizer.deviceId, eldEngineImmobilizer));
    return map;
  }, [data?.eldEngineImmobilizers]);

  const deviceByDriverId = useMemo(() => {
    const map = new Map<number, number>();
    data?.devices?.forEach((device) => {
      getDeviceDrivers(deviceEldDrivers, device)?.forEach((driver) => {
        map.set(driver.id, device.id);
      })
    })
    return map;
  }, [data?.devices, deviceEldDrivers]);

  // Calculate counts for tile filter
  const devicesPerTileFilter: Record<string, number[]> = useMemo(() => {
    const groups: Record<string, number[]> = {
      truck: [],
      trailer: [],
      connected: [],
      disconnected: [],
      driving: [],
      onDuty: [],
      offDuty: [],
      sleeper: [],
    };

    if (!data?.devices) {
      return groups;
    }

    data.devices.forEach((device: DeviceWithMapInfo) => {
      if (device.deviceType.startsWith("ELD Truck #")) {
        groups.truck.push(device.id);
        const connected = !!getDeviceDrivers(deviceEldDrivers, device)?.find(eldDriver => eldDriver.connected);
        if (connected) {
          groups.connected.push(device.id);
        } else {
          groups.disconnected.push(device.id);
        }
      } else {
        groups.trailer.push(device.id);
      }
    });

    if (data.eldDriverLogGroups) {
      data.eldDriverLogGroups.forEach((logGroup) => {
        const deviceId = deviceByDriverId.get(logGroup.eldDriverId);
        if (deviceId) {
          if (["Driving", "Personal Use", "Yard Moves"].includes(logGroup.lastStatus)) {
            groups.driving.push(deviceId);
          } else if (logGroup.lastStatus === "Off Duty") {
            groups.offDuty.push(deviceId);
          } else if (logGroup.lastStatus === "Sleeper") {
            groups.sleeper.push(deviceId);
          } else if (logGroup.lastStatus === "On Duty") {
            groups.onDuty.push(deviceId);
          }
        }
      });
    }

    return groups;
  }, [data?.devices, data?.eldDriverLogGroups, deviceEldDrivers, deviceByDriverId]);

  const filteredDevices = useMemo(() => {
    if (!data?.devices) {
      return [];
    }
    const allDevices = (data.devices || []).filter(device =>
      device.deviceType.toLowerCase().indexOf(searchText.toLowerCase()) > -1 ||
      device.externalIdentifier.toLowerCase().indexOf(searchText.toLowerCase()) > -1
    );

    return allDevices.filter(device => {
      if (selectedType !== "0") {
        if (selectedType === "-1") {
          if (!device.deviceType.startsWith("ELD Truck #")) {
            return false;
          }
        } else if (!(device.externalIdentifier || "").startsWith(`${selectedType}|`)) {
          return false;
        }
      }

      if (tileFilters.size > 0) {
        if (tileFilters.has("trailer")) {
          if (!devicesPerTileFilter.trailer.includes(device.id)) {
            return false;
          }
        } else if (tileFilters.has("truck")) {
          if (!devicesPerTileFilter.truck.includes(device.id)) {
            return false;
          }
        }

        if (tileFilters.has("connected")) {
          if (!devicesPerTileFilter.connected.includes(device.id)) {
            return false;
          }
        } else if (tileFilters.has("disconnected")) {
          if (!devicesPerTileFilter.disconnected.includes(device.id)) {
            return false;
          }
        }

        if (tileFilters.has("driving")) {
          if (!devicesPerTileFilter.driving.includes(device.id)) {
            return false;
          }
        } else if (tileFilters.has("onDuty")) {
          if (!devicesPerTileFilter.onDuty.includes(device.id)) {
            return false;
          }
        } else if (tileFilters.has("offDuty")) {
          if (!devicesPerTileFilter.offDuty.includes(device.id)) {
            return false;
          }
        } else if (tileFilters.has("sleeper")) {
          if (!devicesPerTileFilter.sleeper.includes(device.id)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [data?.devices, searchText, selectedType, tileFilters, devicesPerTileFilter])

  // Tile filter handler
  const handleTileToggle = useCallback((tileId: string) => {
    setSelectedType("0"); // reset sidebar filter
    setTileFilters(prev => {
      const newSet = new Set(prev);

      if (newSet.has(tileId)) {
        newSet.delete(tileId);
      } else {
        const exclusiveGroups = {
          connected: ["trailer", "connected", "disconnected"],
          vehicle: ["trailer", "truck"],
          status: ["trailer", "driving", "onDuty", "offDuty", "sleeper"]
        };

        for (let i = 0; i < Object.entries(exclusiveGroups).length; i+=1) {
          const [groupName, tiles] = Object.entries(exclusiveGroups)[i];
          if (tiles.includes(tileId)) {
            tiles.forEach(tile => newSet.delete(tile));
          }
        }

        newSet.add(tileId);
      }

      return newSet;
    });
  }, []);

  // Map state
  const mapRef = useRef<google.maps.Map | null>(null);

  return (
    <Box display="flex" flexDirection="row" height="100%" marginTop="-15px" position="relative">
      {loading && !data && (
        <Box component="div" sx={{ marginTop: "20vh", textAlign: "center", margin: "auto" }}>
          <CircularProgress size={50} />
        </Box>
      )}

      {!loading && error && (
        <Card sx={{ m: 4, width: "100%", p: 4, textAlign: "center" }}>
          <Typography component="div" padding={2} color="error">
            <Box display="flex" gap={1} justifyContent="center">
              <Iconify icon="octicon:alert-16" /> <i>Error loading the data {error}</i>
            </Box>
          </Typography>
        </Card>
      )}

      {!loading && data && (
        <Box position="absolute" left="0px" top="0px" right="0px" bottom="-15px" display="flex" flexDirection="row">
          <DeviceSidebar
            deviceEldDrivers={deviceEldDrivers}
            mapRef={mapRef}
            updateData={updateData}
            eldEngineImmobilizerByDeviceId={eldEngineImmobilizerByDeviceId}
            onUpdated={() => {
              if (data.devices) {
                updateData("devices")([...data.devices]);
              }
            }}
            processDevicesUpdateResponse={processDevicesUpdateResponse}
            onEldEngineImmobilizerUpdated={(eldEngineImmobilizer: EldEngineImmobilizer) => {
              if (data.eldEngineImmobilizers) {
                updateData("eldEngineImmobilizers")(replaceById(data!.eldEngineImmobilizers, eldEngineImmobilizer));
              }
            }}
            setSelectedUuid={setSelectedUuid}
            filteredDevices={filteredDevices}
            allDevices={data.devices || []}
            searchText={searchText}
            setSearchText={setSearchText}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            deviceHistory={deviceHistory || []}
            setDeviceHistory={setDeviceHistory}
            selectedMarker={selectedMarker}
            viewingMode={viewingMode}
            setViewingMode={setViewingMode}
            deviceForHistory={deviceForHistory}
            setDeviceForHistory={setDeviceForHistory}
            setSelectedVehicles={setSelectedVehicles}
            setShareLocationDialogOpen={setShareLocationDialogOpen}
            handleShareLocationClick={handleShareLocationClick}
            eldVehicles={data.eldVehicles || []}
            eldDriverLogGroups={data.eldDriverLogGroups || []}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
            previousView={previousView}
            setPreviousView={setPreviousView}
            deviceWithDrivers={deviceWithDrivers}
            setDeviceWithDrivers={setDeviceWithDrivers}
            handleTruckMarkerClick={handleTruckMarkerClick}
            setTileFilters={setTileFilters}
          />
          <DeviceMapGoogle
            deviceEldDrivers={deviceEldDrivers}
            handleShareLocationClick={handleShareLocationClick}
            selectedVehicles={selectedVehicles}
            setSelectedVehicles={setSelectedVehicles}
            shareLocationDialogOpen={shareLocationDialogOpen}
            setShareLocationDialogOpen={setShareLocationDialogOpen}
            expirationDate={expirationDate}
            setExpirationDate={setExpirationDate}
            data={data}
            filteredDevices={filteredDevices}
            selectedUuid={selectedUuid}
            deviceHistory={deviceHistory}
            setDeviceHistory={setDeviceHistory}
            setSelectedMarker={setSelectedMarker}
            timezone={timezone}
            processDevicesUpdateResponse={processDevicesUpdateResponse}
            mapRef={mapRef}
            onTruckMarkerClick={handleTruckMarkerClick}
            viewingMode={viewingMode}
            setViewingMode={setViewingMode}
            deviceForHistory={deviceForHistory}
            setDeviceForHistory={setDeviceForHistory}
            dateForHistory={dateForHistory}
            setDateForHistory={setDateForHistory}
            mapOptionsOpen={mapOptionsOpen}
            setMapOptionsOpen={setMapOptionsOpen}
            mapType={mapSettings.mapType}
            setMapType={(type) => updateMapSettings('mapType', type)}
            selectedOptions={mapSettings.selectedOptions}
            setSelectedOptions={(options) => updateMapSettings('selectedOptions', options)}
            tileFilters={tileFilters}
            handleTileToggle={handleTileToggle}
            devicesPerTileFilter={devicesPerTileFilter}
          />
        </Box>
      )}
    </Box>
  );
}

// Helper functions
function isTheSame(lastLat: string, lastLong: string, lat: string, long: string) {
  const lastLatF = Number.parseFloat(lastLat);
  if (Number.isNaN(lastLatF)) return false;
  const lastLongF = Number.parseFloat(lastLong);
  if (Number.isNaN(lastLongF)) return false;
  const latF = Number.parseFloat(lat);
  if (Number.isNaN(latF)) return false;
  const longF = Number.parseFloat(long);
  if (Number.isNaN(longF)) return false;

  const latDiff = Math.abs(lastLatF - latF);
  const lngDiff = Math.abs(lastLongF - longF);
  return latDiff < 0.00035 && lngDiff < 0.00035
}

function escapeHTML(str: string) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getVelocityColor(device: DeviceWithMapInfo) {
  if (overSpeed(device)) {
    return "#ff5631";
  }
  return " #4CAF50";
}

// GoogleMap wrapper component that handles the map ref
export function GoogleMapWrapper({
  mapType,
  mapRef,
  children,
}: {
  mapType: 'default' | 'satellite' | 'terrain';
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  children: React.ReactNode;
}) {
  const map = useMap();

  useEffect(() => {
    if (map) {
      mapRef.current = map;
    }
  }, [map, mapRef]);

  // // Handle map type changes
  const mapTypeId = useMemo(() => {
    if (mapType === 'default') {
      return 'roadmap';
    }
    if (mapType === 'satellite') {
      return "hybrid";
    }
    if (mapType === 'terrain') {
      return "terrain";
    }
    return "";
  }, [mapType]);

  const mapId = useMemo(() => {
    if (mapType === 'satellite' || mapType === 'terrain') {
      return 'regular-google-map';
    }
    return '38c43ef431849e61435e0b20';
  }, [mapType])

  return (
    <GoogleMap
      mapId={mapId}
      style={{ width: '100%', height: '100%' }}
      defaultCenter={{ lat: 40, lng: -96 }}
      defaultZoom={5}
      maxZoom={17}
      gestureHandling="greedy"
      disableDefaultUI
      mapTypeId={mapTypeId}
    >
      {children}
    </GoogleMap>
  );
}

// Video request state types and hook
type VideoRequestStateState = {
  device?: DeviceWithMapInfo,
  liveVideoOpen: boolean,
  livePhotoOpen: boolean,
  requestHistoricVideoOpen: boolean,
  image?: string,
  hls?: string,
}

type VideoRequestState = {
  state: VideoRequestStateState,
  setState: (a: VideoRequestStateState) => void,
  request?: any
}

function useVideoRequestState() {
  const [state, setState] = useState<VideoRequestStateState>({
    device: undefined as DeviceWithMapInfo | undefined,
    liveVideoOpen: false,
    livePhotoOpen: false,
    requestHistoricVideoOpen: false,
    image: undefined as string | undefined,
  })
  const request = useRef(undefined as any);

  return {
    state, setState, request
  }
}

// DeviceSidebar component
function DeviceSidebar({
  deviceEldDrivers,
  filteredDevices,
  allDevices,
  processDevicesUpdateResponse,
  onEldEngineImmobilizerUpdated,
  eldEngineImmobilizerByDeviceId,
  onUpdated,
  updateData,
  setSelectedUuid,
  mapRef,
  searchText,
  setSearchText,
  selectedType,
  setSelectedType,
  deviceHistory,
  setDeviceHistory,
  selectedMarker,
  viewingMode,
  setViewingMode,
  deviceForHistory,
  setDeviceForHistory,
  setSelectedVehicles,
  setShareLocationDialogOpen,
  handleShareLocationClick,
  eldVehicles,
  eldDriverLogGroups,
  selectedView,
  setSelectedView,
  previousView,
  setPreviousView,
  deviceWithDrivers,
  setDeviceWithDrivers,
  handleTruckMarkerClick,
  setTileFilters,
}: {
  deviceEldDrivers: DeviceEldDriver[],
  filteredDevices: DeviceWithMapInfo[],
  allDevices: DeviceWithMapInfo[],
  updateData: any,
  processDevicesUpdateResponse: (a: any) => void,
  onEldEngineImmobilizerUpdated: (a: EldEngineImmobilizer) => void,
  eldEngineImmobilizerByDeviceId: Map<number, EldEngineImmobilizer>,
  searchText: string,
  setSearchText: (a: string) => void,
  selectedType: string,
  setSelectedType: (a: string) => void,
  mapRef: React.MutableRefObject<google.maps.Map | null>,
  onUpdated: VoidFunction,
  setSelectedUuid: (a: string) => void,
  deviceHistory: DeviceHistoryLineWithCityNameGroup[],
  setDeviceHistory: (history: DeviceHistoryLineWithCityNameGroup[]) => void,
  selectedMarker: number,
  viewingMode: "sidebar" | "history",
  setViewingMode: (mode: "sidebar" | "history") => void,
  deviceForHistory: DeviceWithMapInfo | undefined,
  setDeviceForHistory: (device: DeviceWithMapInfo | undefined) => void,
  setShareLocationDialogOpen: (open: boolean) => void,
  setSelectedVehicles: (vehicles: DeviceWithMapInfo[]) => void,
  handleShareLocationClick: (vehicle?: DeviceWithMapInfo) => void,
  eldVehicles: EldVehicle[],
  eldDriverLogGroups: EldDriverLogGroup[],
  selectedView: "vehicles" | "drivers" | "deviceWithDrivers",
  setSelectedView: (view: "vehicles" | "drivers" | "deviceWithDrivers") => void,
  previousView: "vehicles" | "drivers",
  setPreviousView: (view: "vehicles" | "drivers") => void,
  deviceWithDrivers: DeviceWithMapInfo | undefined,
  setDeviceWithDrivers: (device: DeviceWithMapInfo | undefined) => void,
  handleTruckMarkerClick: (device: DeviceWithMapInfo, drivers: DeviceEldDriver[]) => void,
  setTileFilters: (filters: Set<string>) => void,
}) {
  const collapsed = viewingMode !== "sidebar";
  const { session } = useAuthContext();
  const [selectedDevice, setSelectedDevice] = useState<DeviceWithMapInfo>();
  const [loading, setLoading] = useState(false);
  const [newDeviceType, setNewDeviceType] = useState("");

  const timezone = session?.organization?.companyTimezone as string;

  const clearVehicleInfo = useCallback(() => {
    setDeviceWithDrivers(undefined);
  }, [setDeviceWithDrivers]);

  const videoRequestState = useVideoRequestState();

  return <Box sx={{
    position: {md: "relative", xs: "absolute"},
    top: {xs: "0px", md: null},
    bottom: {xs: "0px", md: null},
    left: {xs: "0px", md: null},
    right: collapsed ? undefined : {xs: "40px", md: null},
    zIndex: 1200,
  }}>
    <Box sx={{ background: "#ffffff", boxShadow: "5px 0 7px -7px rgba(0, 0, 0, 0.5)", zIndex: 0, overflowY: "auto", overscrollBehavior: "none", height: "100%", width: (collapsed ? "0px" : { xs: "calc(100% -30px)", md: "300px" }) }} display="flex" flexDirection="column">
      {selectedView !== "deviceWithDrivers" && (
        <Box p={1} display="flex" gap={1} sx={{ borderBottom: "1px solid #EEE" }}>
          <Box flex={selectedView === "drivers" ? 1 : 2}>
            <TruxSimpleTextField
              value={searchText}
              onChange={setSearchText}
              label={selectedView === "drivers" ? "Search Drivers" : "Search Devices"}
            />
          </Box>
          {selectedView !== "drivers" && (
            <Box flex={1}>
              <TruxSimpleSelectField value={selectedType} onChange={val => { setSelectedType(val); setTileFilters(new Set<string>()); }}>
                <MenuItem value="0" selected={selectedType === "0"}>All</MenuItem>
                <MenuItem value="-1" selected={selectedType === "-1"}>Trucks</MenuItem>
                <MenuItem value="1" selected={selectedType === "1"}>CT Trailers</MenuItem>
                <MenuItem value="7" selected={selectedType === "7"}>CT Rented</MenuItem>
                <MenuItem value="2" selected={selectedType === "2"}>Samsara</MenuItem>
                <MenuItem value="3" selected={selectedType === "3"}>SkyBitz</MenuItem>
                <MenuItem value="4" selected={selectedType === "4"}>Premier</MenuItem>
                <MenuItem value="5" selected={selectedType === "5"}>CT Immobilizer</MenuItem>
                <MenuItem value="6" selected={selectedType === "6"}>Anytrek</MenuItem>
              </TruxSimpleSelectField>
            </Box>
          )}
        </Box>
      )}
      <Box flexGrow={1} sx={{ display: "flex", flexDirection: "column" }}>
        {((selectedView === "vehicles" && filteredDevices.length === 0) ||
          (selectedView === "drivers" && deviceEldDrivers.length === 0)) && <Box pl={1}>
          <Typography component="div" padding={2} color="grey">
            <Box display="flex" gap={1}>
              <Iconify icon="octicon:alert-16" />
              {selectedView === "drivers" ? "No Drivers Found" : "No Devices Found"}
            </Box>
          </Typography>
        </Box>}
        <Box sx={{ position: "relative", flex: 1 }}>
          <Box sx={{ position: "absolute", left: "0px", right: "0px", top: "0px", bottom: "0px" }}>
            {selectedView === "vehicles" && <VirtualizedDeviceList
              deviceEldDrivers={deviceEldDrivers}
              processDevicesUpdateResponse={processDevicesUpdateResponse}
              onEldEngineImmobilizerUpdated={onEldEngineImmobilizerUpdated}
              setSelectedVehicles={setSelectedVehicles}
              setShareLocationDialogOpen={setShareLocationDialogOpen}
              updateData={updateData}
              eldEngineImmobilizerByDeviceId={eldEngineImmobilizerByDeviceId}
              filteredDevices={filteredDevices}
              mapRef={mapRef}
              setSelectedDevice={setSelectedDevice}
              setNewDeviceType={setNewDeviceType}
              setSelectedUuid={setSelectedUuid}
              timezone={timezone}
              setDeviceForHistory={setDeviceForHistory}

              setLiveVideoOpen={(liveVideoOpen: boolean, device: DeviceWithMapInfo) => {
                videoRequestState.setState({ ...videoRequestState.state, device, liveVideoOpen, hls: undefined })

                const liveVideoRequest = axios.post(endpoints.cameras.liveVideo, JSON.stringify({ deviceId: device.id }));
                videoRequestState.request.current = liveVideoRequest;

                liveVideoRequest.then(response => {
                  if (videoRequestState.request.current === liveVideoRequest) {
                    if (response.data?.hls) {
                      setTimeout(() => {
                        videoRequestState.setState(s => ({ ...s, hls: response.data?.hls }));
                      }, 2000);
                    } else {
                      enqueueSnackbar("Live Video could not be started. Please try later.", { variant: "error" });
                      videoRequestState.setState(s => ({ ...s, liveVideoOpen: false }))
                    }
                  }
                }).catch(() => {
                  enqueueSnackbar("An unexpected error has occured. Please contact customer support.", { variant: "error" });
                  videoRequestState.setState(s => ({ ...s, liveVideoOpen: false }))
                });
              }}


              setLivePhotoOpen={(livePhotoOpen: boolean, device: DeviceWithMapInfo) => {
                videoRequestState.setState({ ...videoRequestState.state, device, livePhotoOpen, image: undefined })

                const livePhotoRequest = axios.post(endpoints.cameras.livePhoto, JSON.stringify({ deviceId: device.id }));
                videoRequestState.request.current = livePhotoRequest;

                  livePhotoRequest.then(response => {
                    if (videoRequestState.request.current === livePhotoRequest) {
                      if (response.data?.image) {
                        videoRequestState.setState(s => ({ ...s, image: response.data?.image }));
                      } else {
                        enqueueSnackbar("Image could not be loaded. Please try later.", { variant: "error" });
                        videoRequestState.setState(s => ({ ...s, livePhotoOpen: false }))
                      }
                    }
                  }).catch(() => {
                    enqueueSnackbar("An unexpected error has occured. Please contact customer support.", { variant: "error" });
                    videoRequestState.setState(s => ({ ...s, livePhotoOpen: false }))
                  });
                }}
                setRequestHistoricVideoOpen={(requestHistoricVideoOpen: boolean, device: DeviceWithMapInfo) => videoRequestState.setState({ ...videoRequestState.state, device, requestHistoricVideoOpen })}
                setViewingMode={setViewingMode}
                onTruckMarkerClick={handleTruckMarkerClick}
                eldDriverLogGroups={eldDriverLogGroups}
              />}
              {selectedView === "drivers" && <VirtualizedDriversList
                deviceEldDrivers={deviceEldDrivers}
                allDevices={allDevices}
                timezone={timezone}
                mapRef={mapRef}
                setSelectedUuid={setSelectedUuid}
                handleTruckMarkerClick={handleTruckMarkerClick}
                searchText={searchText}
                eldDriverLogGroups={eldDriverLogGroups}
              />}
              {selectedView === "deviceWithDrivers" && deviceWithDrivers && (
                <VehicleInfo
                  deviceEldDrivers={deviceEldDrivers}
                  selectedDevice={deviceWithDrivers}
                  handleShareLocationClick={handleShareLocationClick}
                  onClose={() => {
                    clearVehicleInfo();
                    setSelectedView(previousView);
                  }}
                  timezone={timezone}
                  eldEngineImmobilizer={eldEngineImmobilizerByDeviceId.get(deviceWithDrivers.id)}
                  eldVehicles={eldVehicles}
                  eldDriverLogGroups={eldDriverLogGroups}
                  setDeviceForHistory={setDeviceForHistory}
                  setViewingMode={setViewingMode}
                />
              )}
            </Box>
          </Box>
        </Box>
             {selectedView !== "deviceWithDrivers" && (
          <Box display="flex" p="4px" gap="4px" boxShadow="1px 1px 1px #ddd" borderTop="1px solid #ddd">
            <Tooltip title="Vehicles"><Button sx={{
              flexGrow:1,
              backgroundColor: selectedView === "vehicles" ? "#000000" : "transparent",
              color: selectedView === "vehicles" ? "#ffffff" : "inherit",
              '&:hover': {
                backgroundColor: selectedView === "vehicles" ? "#333333" : "rgba(0, 0, 0, 0.04)"
              }
            }} onClick={() => {
              if (selectedView === "drivers") {
                setPreviousView("drivers");
              }
              setSearchText("");
              setSelectedView("vehicles");
            }}><Iconify icon="mdi:car" /></Button></Tooltip>
            <Tooltip title="Drivers"><Button sx={{
              flexGrow:1,
              backgroundColor: selectedView === "drivers" ? "#000000" : "transparent",
              color: selectedView === "drivers" ? "#ffffff" : "inherit",
              '&:hover': {
                backgroundColor: selectedView === "drivers" ? "#333333" : "rgba(0, 0, 0, 0.04)"
              }
            }} onClick={() => {
              if (selectedView === "vehicles") {
                setPreviousView("vehicles");
              }
              setSearchText("");
              setSelectedView("drivers");
            }}><Iconify icon="mdi:drivers-license" /></Button></Tooltip>
          </Box>
        )}

      {selectedDevice && <SimpleDialog
        title="Update Asset"
        open={!!selectedDevice}
        onCancel={() => {
          setSelectedDevice(undefined);
        }}
        onSubmit={() => {
          setLoading(true);

          axios.post(endpoints.devices.updateDeviceType, {
            deviceId: selectedDevice.id,
            deviceType: newDeviceType,
          }).then(() => {
            enqueueSnackbar("Successfully updated the device!");
            selectedDevice.deviceType = newDeviceType;
            onUpdated();
          }).finally(() => {
            setSelectedDevice(undefined);
            setLoading(false);
          })
        }}
        submitDisabled={loading}
        submitButton="Update Asset"
        submitButtonIcon={<Iconify icon="basil:save-solid" />}
        submitButtonColor="info">

        <Stack direction="column" gap={1}>
          <TruxFormField label="Device Label">
            <TruxSimpleTextField value={newDeviceType} onChange={(val) => setNewDeviceType(val)} />
          </TruxFormField>
        </Stack>
      </SimpleDialog >}
    </Box>

    <Box sx={{
      position: "absolute",
      top: {xs: collapsed ? "calc(50% - 40px)" : "0px", md: "calc(50% - 25px)" },
      bottom: {xs: collapsed ? undefined : "0px", md: "initial"},
      borderLeft: {xs: collapsed ? "none" : "1px solid #DDD", md: "none"},
      display: "flex",
      alignItems: "center",
      right: {xs: "-41px", md: "-25px"},
      zIndex: 1000,
      padding: {xs: "10px", md: "2px"},
      paddingTop: {xs: "40px", md: "20px"},
      paddingBottom: {xs: "40px", md: "20px"},
      borderTopRightRadius: "4px",
      borderBottomRightRadius: "4px",
      background: "#fafafa",
      border: "1px solid #e5e6e6",
      boxShadow: {xs: "4px 3px 5px #AAAAAA55", md: "none"}
    }} onClick={() => {
      if (viewingMode === "history") {
        setDeviceForHistory(undefined);
        setDeviceHistory([]);
      }
      setViewingMode(viewingMode === "sidebar" ? "history" : "sidebar");
    }}>
      {collapsed ? <Iconify icon="line-md:chevron-right" /> : <Iconify icon="line-md:chevron-left" />}
    </Box>

    {videoRequestState.state.livePhotoOpen
      && videoRequestState.state.device
      && <LivePhoto videoRequestState={videoRequestState} />
    }
    {videoRequestState.state.liveVideoOpen
      && videoRequestState.state.device
      && <LiveVideo videoRequestState={videoRequestState} />
    }
    {videoRequestState.state.requestHistoricVideoOpen
      && videoRequestState.state.device
      && <RequestHistoricVideo videoRequestState={videoRequestState} />
    }
  </Box>
}

// Supporting components for sidebar
type VirtualizedDeviceListProps = {
  deviceEldDrivers: DeviceEldDriver[],
  processDevicesUpdateResponse: (a: any) => void,
  onEldEngineImmobilizerUpdated: (a: EldEngineImmobilizer) => void,
  setSelectedVehicles: (vehicles: DeviceWithMapInfo[]) => void,
  setShareLocationDialogOpen: (open: boolean) => void,
  updateData: any,
  eldEngineImmobilizerByDeviceId: Map<number, EldEngineImmobilizer>,
  filteredDevices: DeviceWithMapInfo[],
  mapRef: React.MutableRefObject<google.maps.Map | null>,
  setSelectedDevice: React.Dispatch<React.SetStateAction<DeviceWithMapInfo | undefined>>,
  setNewDeviceType: (a: string) => void,
  setSelectedUuid: (a: string) => void,
  timezone: string,
  setDeviceForHistory: (device: DeviceWithMapInfo) => void,
  setLiveVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setLivePhotoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setRequestHistoricVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setViewingMode: (mode: "sidebar" | "history") => void,
  onTruckMarkerClick: (device: DeviceWithMapInfo, drivers: DeviceEldDriver[]) => void,
  eldDriverLogGroups: EldDriverLogGroup[],
}

function VirtualizedDeviceList({
  deviceEldDrivers,
  processDevicesUpdateResponse,
  onEldEngineImmobilizerUpdated,
  setSelectedVehicles,
  setShareLocationDialogOpen,
  updateData,
  eldEngineImmobilizerByDeviceId,
  filteredDevices,
  mapRef,
  setSelectedDevice,
  setNewDeviceType,
  setSelectedUuid,
  timezone,
  setDeviceForHistory,
  setLiveVideoOpen,
  setLivePhotoOpen,
  setRequestHistoricVideoOpen,
  setViewingMode,
  onTruckMarkerClick,
  eldDriverLogGroups,
}: VirtualizedDeviceListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredDevices.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 95,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  return <SimpleBar
    scrollableNodeProps={{ ref: parentRef }}
    style={{
    height: '100%',
    overflowX: 'hidden',
    overscrollBehavior: 'none',
    padding: '4px 0',
  }}>
    <div
      style={{
        height: rowVirtualizer.getTotalSize(),
        width: '100%',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
        }}
      >
        {virtualItems.map((virtualRow) => (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={rowVirtualizer.measureElement}
          >
            <DeviceBlock
              deviceEldDrivers={deviceEldDrivers}
              processDevicesUpdateResponse={processDevicesUpdateResponse}
              onEldEngineImmobilizerUpdated={onEldEngineImmobilizerUpdated}
              setSelectedVehicles={setSelectedVehicles}
              setShareLocationDialogOpen={setShareLocationDialogOpen}
              updateData={updateData}
              eldEngineImmobilizer={eldEngineImmobilizerByDeviceId.get(filteredDevices[virtualRow.index].id)}
              device={filteredDevices[virtualRow.index]}
              mapRef={mapRef}
              setSelectedDevice={setSelectedDevice}
              setNewDeviceType={setNewDeviceType}
              setSelectedUuid={setSelectedUuid}
              timezone={timezone}
              setDeviceForHistory={setDeviceForHistory}
              setLiveVideoOpen={setLiveVideoOpen}
              setLivePhotoOpen={setLivePhotoOpen}
              setRequestHistoricVideoOpen={setRequestHistoricVideoOpen}
              setViewingMode={setViewingMode}
              onTruckMarkerClick={onTruckMarkerClick}
              eldDriverLogGroups={eldDriverLogGroups}
            />
          </div>
        ))}
      </div>
    </div>
  </SimpleBar>
}

type VirtualizedDriversListProps = {
  deviceEldDrivers: DeviceEldDriver[],
  allDevices: DeviceWithMapInfo[],
  timezone: string,
  mapRef: React.MutableRefObject<google.maps.Map | null>,
  setSelectedUuid: (a: string) => void,
  handleTruckMarkerClick: (device: DeviceWithMapInfo, drivers: DeviceEldDriver[]) => void,
  searchText: string,
  eldDriverLogGroups: EldDriverLogGroup[],
}

function VirtualizedDriversList({
  deviceEldDrivers,
  allDevices,
  timezone,
  mapRef,
  setSelectedUuid,
  handleTruckMarkerClick,
  searchText,
  eldDriverLogGroups,
}: VirtualizedDriversListProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const filteredDrivers = useMemo(() => {
    if (!searchText) return deviceEldDrivers;

    return deviceEldDrivers.filter(driver => {
      const fullName = `${driver.firstName || ''} ${driver.lastName || ''}`.trim().toLowerCase();
      const email = (driver.email || '').toLowerCase();
      const username = (driver.username || '').toLowerCase();
      const searchLower = searchText.toLowerCase();

      return fullName.includes(searchLower) ||
             email.includes(searchLower) ||
             username.includes(searchLower);
    });
  }, [deviceEldDrivers, searchText]);

  const rowVirtualizer = useVirtualizer({
    count: filteredDrivers.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 85,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  if (filteredDrivers.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography component="div" padding={2} color="grey">
          <Box display="flex" gap={1} justifyContent="center">
            <Iconify icon="octicon:alert-16" />
            No Drivers Found
          </Box>
        </Typography>
      </Box>
    );
  }

  return <SimpleBar
    scrollableNodeProps={{ ref: parentRef }}
    style={{
    height: '100%',
    overflowX: 'hidden',
    overscrollBehavior: 'none',
  }}>
    <div
      style={{
        height: rowVirtualizer.getTotalSize(),
        width: '100%',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
        }}
      >
        {virtualItems.map((virtualRow) => (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={rowVirtualizer.measureElement}
          >
            <DriverBlock
              deviceEldDrivers={deviceEldDrivers}
              driver={filteredDrivers[virtualRow.index]}
              allDevices={allDevices}
              timezone={timezone}
              mapRef={mapRef}
              setSelectedUuid={setSelectedUuid}
              handleTruckMarkerClick={handleTruckMarkerClick}
              eldDriverLogGroups={eldDriverLogGroups}
            />
          </div>
        ))}
      </div>
    </div>
  </SimpleBar>
}

type DeviceBlockType = {
  deviceEldDrivers: DeviceEldDriver[],
  updateData: any,
  device: DeviceWithMapInfo,
  processDevicesUpdateResponse: (a: any) => void,
  onEldEngineImmobilizerUpdated: (a: EldEngineImmobilizer) => void,
  setSelectedVehicles: (vehicles: DeviceWithMapInfo[]) => void,
  setShareLocationDialogOpen: (open: boolean) => void,
  mapRef: React.MutableRefObject<google.maps.Map | null>,
  setSelectedDevice: (a: DeviceWithMapInfo) => void,
  setNewDeviceType: (a: string) => void,
  setSelectedUuid: (a: string) => void,
  timezone: string,
  setDeviceForHistory: (device: DeviceWithMapInfo) => void,
  eldEngineImmobilizer?: EldEngineImmobilizer,
  setLiveVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setLivePhotoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setRequestHistoricVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setViewingMode: (mode: "sidebar" | "history") => void,
  onTruckMarkerClick: (device: DeviceWithMapInfo, drivers: DeviceEldDriver[]) => void,
  eldDriverLogGroups: EldDriverLogGroup[],
};

function DeviceBlock({
  deviceEldDrivers,
  device,
  mapRef,
  processDevicesUpdateResponse,
  onEldEngineImmobilizerUpdated,
  setSelectedVehicles,
  setShareLocationDialogOpen,
  updateData,
  eldEngineImmobilizer,
  setSelectedDevice,
  setNewDeviceType,
  setSelectedUuid,
  setDeviceForHistory,
  timezone,
  setLiveVideoOpen,
  setLivePhotoOpen,
  setRequestHistoricVideoOpen,
  setViewingMode,
  onTruckMarkerClick,
  eldDriverLogGroups
}: DeviceBlockType) {
  const eldDriversForDevice = getDeviceDrivers(deviceEldDrivers, device);
  const connected = !!eldDriversForDevice?.find(eldDriver => eldDriver.connected);
  const isOnline = !!eldDriversForDevice;
  const isTruck = device.deviceType.startsWith("ELD Truck #");
  const isOverSpeed = device.velocity !== undefined && overSpeed(device);

  const getDriverLogGroup = (driverId: number) => eldDriverLogGroups?.find(log => log.eldDriverId === driverId);

  const drivingDrivers = eldDriversForDevice?.filter(driver => {
    const logGroup = getDriverLogGroup(driver.id);
    return logGroup?.lastStatus === "Driving";
  }) || [];

  return (
    <Box sx={{ px: 1, py: 0.25 }}>
      <Card sx={{
        p: 1.5,
        display: "flex",
        border: !isTruck || connected ? "1px solid #E9ECEF" : "none",
        borderRadius: "8px",
        justifyContent: "space-between",
        flexDirection: "column",
        backgroundColor: isOnline ? "#FFFFFF" : "#FFF8E1",
        boxShadow: isTruck && !connected ? "0px 0px 2px #cb3736" : "0 1px 3px rgba(0,0,0,0.02)",
        transition: "all 0.2s ease-in-out",
        '&:hover': device.lastPing && {
          cursor: "pointer",
          backgroundColor: "#F8F9FA",
          borderColor: "#D1D5DB",
          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
          transform: "translateY(-1px)"
        },
        position: "relative",
        mb: 0.5
      }} onClick={() => {
        if (!device.lastPing) {
          return;
        }

        // Default behavior: Use Google Maps API to pan to device and open InfoWindow
        if (mapRef.current && device.lastPosLat && device.lastPosLong) {
          const targetPosition = {
            lat: Number(device.lastPosLat),
            lng: Number(device.lastPosLong)
          };

          mapRef.current.panTo(targetPosition);
          if ((mapRef.current.getZoom() || 100) < 12) {
            mapRef.current.setZoom(12);
          }

        if (onTruckMarkerClick) {
          onTruckMarkerClick(device, eldDriversForDevice || []);
          return;
        }

          // Set selected UUID to open InfoWindow
          setTimeout(() => {
            setSelectedUuid(device.deviceUuid);
          }, 51);
        }
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#1D2939",
              lineHeight: 1.2,
              mb: 0.25
            }}>
              {device.externalIdentifier ? (
                <Link
                  color="#1D2939"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDevice(device);
                    setNewDeviceType(device.deviceType);
                  }}
                  sx={{
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                      color: "#3B82F6"
                    }
                  }}
                >
                  {device.deviceType}
                </Link>
              ) : (
                device.deviceType.replace("ELD Truck", "Truck")
              )}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: "10px" }}>
              {device.lastPing ? (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2" sx={{
                      fontSize: "10px",
                      color: "#6B7280",
                      lineHeight: 1.1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}>
                      {device.location || "Location unavailable"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2" sx={{
                      fontSize: "10px",
                      color: "#6B7280",
                      lineHeight: 1.1
                    }}>
                      {formatDatetimeForPreview(device.lastPing, timezone)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    {isTruck && (
                      <Chip
                        size="small"
                        label={connected ? "Connected" : "Disconnected"}
                        icon={<Iconify icon={connected ? "ph:cell-signal-full" : "ph:cell-signal-slash"} sx={{height: "15px"}} />}
                        sx={{
                          height: "18px",
                          fontSize: "9px",
                          fontWeight: 500,
                          backgroundColor: connected ? "#E3F2FD" : "#FFEBEE",
                          color: connected ? "#1565C0" : "#C62828",
                          pointerEvents: !connected ? "none" : "auto",
                          "& .MuiChip-icon": {
                            fontSize: "12px",
                            color: connected ? "#1565C0" : "#ff5631"
                          }
                        }}
                      />
                    )}

                    {isOverSpeed && (
                      <Chip
                        size="small"
                        label={`${device.velocity!.toFixed(0)} mph`}
                        icon={<Iconify icon="mdi:speedometer" />}
                        sx={{
                          height: "18px",
                          fontSize: "9px",
                          fontWeight: 600,
                          backgroundColor: "#FFEBEE",
                          color: "#C62828",
                          pointerEvents: "none",
                          "& .MuiChip-icon": {
                            fontSize: "12px",
                            color: "#ff5631"
                          }
                        }}
                      />
                    )}

                    {device.camera && (
                      <Chip
                        size="small"
                        label="Camera"
                        icon={<Iconify icon="qlementine-icons:camera-16" />}
                        sx={{
                          height: "18px",
                          fontSize: "9px",
                          fontWeight: 500,
                          backgroundColor: "#E3F2FD",
                          color: "#1565C0",
                          pointerEvents: "none",
                          "& .MuiChip-icon": {
                            fontSize: "10px",
                            color: "#1565C0"
                          }
                        }}
                      />
                    )}

                    {device.externalIdentifier && !device.externalIdentifier.startsWith("2|") && device.battery !== undefined && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        <Iconify
                          icon={device.battery > 20 ? "mdi:battery" : "mdi:battery-low"}
                          sx={{
                            fontSize: "12px",
                            color: device.battery > 20 ? "#4CAF50" : "#FF9800",
                            flexShrink: 0
                          }}
                        />
                        <Typography variant="body2" sx={{
                          fontSize: "10px",
                          color: "#6B7280",
                          lineHeight: 1.1
                        }}>
                          {device.battery}%
                        </Typography>
                      </Box>
                    )}

                    {isTruck && eldDriversForDevice && eldDriversForDevice.length > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        <Iconify
                          icon="mdi:account-group"
                          sx={{
                            fontSize: "12px",
                            color: "#6B7280",
                            flexShrink: 0
                          }}
                        />
                        <Typography variant="body2" sx={{
                          fontSize: "10px",
                          color: "#6B7280",
                          lineHeight: 1.1
                        }}>
                          {eldDriversForDevice.length} driver{eldDriversForDevice.length !== 1 ? 's' : ''}
                          {eldDriversForDevice.some(driver => driver.connected) && (
                            <span style={{ color: '#4CAF50', marginLeft: '2px' }}>
                              • {eldDriversForDevice.filter(driver => driver.connected).length} online
                            </span>
                          )}
                          {drivingDrivers.length > 0 && (
                            <span style={{ color: '#FF9800', marginLeft: '2px' }}>
                              • {drivingDrivers.length} driving
                            </span>
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Iconify icon="mdi:help-circle-outline" sx={{ fontSize: "12px", color: "#FF9800" }} />
                  <Typography variant="body2" sx={{
                    fontSize: "10px",
                    color: "#F57C00",
                    fontStyle: "italic"
                  }}>
                    Last position unknown
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25, flexShrink: 0 }}>
            {device.camera && (
              <CameraMenu
                device={device}
                setLiveVideoOpen={setLiveVideoOpen}
                setLivePhotoOpen={setLivePhotoOpen}
                setRequestHistoricVideoOpen={setRequestHistoricVideoOpen}
              />
            )}
            <DeviceMoreButton
              processDevicesUpdateResponse={processDevicesUpdateResponse}
              onEldEngineImmobilizerUpdated={onEldEngineImmobilizerUpdated}
              updateData={updateData}
              setShareLocationDialogOpen={setShareLocationDialogOpen}
              setSelectedVehicles={setSelectedVehicles}
              eldEngineImmobilizer={eldEngineImmobilizer}
              device={device}
              setDeviceForHistory={setDeviceForHistory}
              setViewingMode={setViewingMode}
              mapRef={mapRef}
            />
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

type DriverBlockType = {
  deviceEldDrivers: DeviceEldDriver[],
  driver: DeviceEldDriver,
  allDevices: DeviceWithMapInfo[],
  timezone: string,
  mapRef: React.MutableRefObject<google.maps.Map | null>,
  setSelectedUuid: (a: string) => void,
  handleTruckMarkerClick: (device: DeviceWithMapInfo, drivers: DeviceEldDriver[]) => void,
  eldDriverLogGroups: EldDriverLogGroup[],
};

function DriverBlock({
  deviceEldDrivers,
  driver,
  allDevices,
  timezone,
  mapRef,
  setSelectedUuid,
  handleTruckMarkerClick,
  eldDriverLogGroups
}: DriverBlockType) {
  const device = allDevices.find((d: DeviceWithMapInfo) => getDeviceDrivers(deviceEldDrivers, d).some((eldDriver: DeviceEldDriver) => eldDriver.id === driver.id)
  );

  const getDriverLogGroup = (driverId: number) => eldDriverLogGroups?.find(log => log.eldDriverId === driverId);

  const driverLogGroup = getDriverLogGroup(driver.id);

  return (
    <Box sx={{ px: 1, py: 0.25 }}>
      <Card sx={{
        p: 1.5,
        display: "flex",
        border: "1px solid #E9ECEF",
        borderRadius: "8px",
        justifyContent: "space-between",
        flexDirection: "column",
        backgroundColor: device?.lastPing ? "#FFFFFF" : "#FFF8E1",
        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
        transition: "all 0.2s ease-in-out",
        '&:hover': device?.lastPing && {
          cursor: "pointer",
          backgroundColor: "#F8F9FA",
          borderColor: "#D1D5DB",
          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
          transform: "translateY(-1px)"
        },
        position: "relative",
        mb: 0.5
      }} onClick={() => {
        if (!device?.lastPing) {
          return;
        }

        if (mapRef.current && device.lastPosLat && device.lastPosLong) {
          const targetPosition = {
            lat: Number(device.lastPosLat),
            lng: Number(device.lastPosLong)
          };

          mapRef.current.panTo(targetPosition);
          if ((mapRef.current.getZoom() || 100) < 12) {
            mapRef.current.setZoom(12);
          }

          handleTruckMarkerClick(device, getDeviceDrivers(deviceEldDrivers, device) || []);
        }
      }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.25 }}>
            <Typography variant="body2" fontWeight={600} sx={{
              fontSize: "13px",
              color: "#1D2939",
              lineHeight: 1.2
            }}>
              {driver.firstName && driver.lastName
                ? `${driver.firstName} ${driver.lastName}`
                : `Driver ${driver.id}`
              }
            </Typography>
            {driverLogGroup?.lastStatus && (
              <EldDriverStatusLabel
                status={driverLogGroup.lastStatus}
              />
            )}
          </Box>

          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            py: 0.25,
            px: 0.75,
            backgroundColor: device ? "#F0F7FF" : "#FFF8E1",
            borderRadius: "4px",
            border: device ? "1px solid #E3F2FD" : "1px solid #FFF3C4"
          }}>
            <Box sx={{
              width: "5px",
              height: "5px",
              borderRadius: "50%",
              backgroundColor: device ? "#2196F3" : "#FF9800",
              flexShrink: 0
            }} />
            <Typography variant="caption" sx={{
              fontSize: "10px",
              fontWeight: 500,
              color: device ? "#1565C0" : "#E65100",
              lineHeight: 1.1
            }}>
              {device ? `${device.deviceType}` : "No vehicle assigned"}
            </Typography>
          </Box>

          {driverLogGroup && (
            <Box sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 0.5,
              backgroundColor: "#F8FAFC",
              borderRadius: "6px",
              p: 1,
              border: "1px solid #E9ECEF",
              mt: 0.25
            }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{
                  fontSize: "8px",
                  fontWeight: 600,
                  color: "#6B7280",
                  display: "block",
                  mb: 0.25,
                  letterSpacing: "0.3px"
                }}>
                  BREAK
                </Typography>
                <Typography variant="body2" sx={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: "#1F2937",
                  lineHeight: 1
                }}>
                  {formatTimeAsHourMin(driverLogGroup.breakTime)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{
                  fontSize: "8px",
                  fontWeight: 600,
                  color: "#6B7280",
                  display: "block",
                  mb: 0.25,
                  letterSpacing: "0.3px"
                }}>
                  DRIVE
                </Typography>
                <Typography variant="body2" sx={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: "#1F2937",
                  lineHeight: 1
                }}>
                  {formatTimeAsHourMin(driverLogGroup.driveTime)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{
                  fontSize: "8px",
                  fontWeight: 600,
                  color: "#6B7280",
                  display: "block",
                  mb: 0.25,
                  letterSpacing: "0.3px"
                }}>
                  SHIFT
                </Typography>
                <Typography variant="body2" sx={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: "#1F2937",
                  lineHeight: 1
                }}>
                  {formatTimeAsHourMin(driverLogGroup.shiftTime)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{
                  fontSize: "8px",
                  fontWeight: 600,
                  color: "#6B7280",
                  display: "block",
                  mb: 0.25,
                  letterSpacing: "0.3px"
                }}>
                  CYCLE
                </Typography>
                <Typography variant="body2" sx={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: "#1F2937",
                  lineHeight: 1
                }}>
                  {formatTimeAsHourMin(driverLogGroup.cycleTime)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
}

function CameraMenu({
  device,
  setLiveVideoOpen,
  setLivePhotoOpen,
  setRequestHistoricVideoOpen
}: {
  device: DeviceWithMapInfo,
  setLiveVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setLivePhotoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
  setRequestHistoricVideoOpen: (a: boolean, device: DeviceWithMapInfo) => void,
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return <>
    <IconButton onClick={e => {
      setAnchorEl(e.currentTarget);
      e.stopPropagation();
      e.preventDefault();
    }}>
      <Iconify icon="qlementine-icons:camera-16" />
    </IconButton>
    {anchorEl && <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={() => setAnchorEl(null)}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
    >

      <MenuItem onClick={(e) => {
        setAnchorEl(null);
        setLiveVideoOpen(true, device);
      }}>Live Video</MenuItem>

      <MenuItem onClick={(e) => {
        setAnchorEl(null);
        setLivePhotoOpen(true, device);
      }}>Live Photo</MenuItem>

      <MenuItem onClick={(e) => {
        setAnchorEl(null);
        setRequestHistoricVideoOpen(true, device);
      }}>Request Historic Video</MenuItem>
    </Menu>}
  </>
}

type RequestDialog = {
  videoRequestState: VideoRequestState;
};

function LivePhoto({videoRequestState}: RequestDialog) {
  const open= videoRequestState.state.livePhotoOpen;
  const setOpen= (livePhotoOpen: boolean) => videoRequestState.setState({...videoRequestState.state, livePhotoOpen});

  return <SimpleDialog
    title="Live Photo"
    open={open}
    onCancel={() => setOpen(false)}
    sx={{".MuiDialogContent-root": {overflowY: "visible"}}}
  >
    {videoRequestState.state.image ? <img src={videoRequestState.state.image} alt="truck" style={{width: "100%"}} onError={(e) => {
        if (!(e.target as any).attempts) {
          (e.target as any).attempts = 0;
        }
        (e.target as any).attempts+=1;

        if ((e.target as any).attempts < 3) {
          setTimeout(() => {
            (e.target as any).src = (e.target as any).src;
          }, 1000 * (e.target as any).attempts);
        }
      }} /> : <>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
        <CircularProgress />
      </Box>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", pt: 2}}>
        Taking the photo...
      </Box>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
        <i>Please be patient. This can take up to one minute.</i>
      </Box>
    </>}
  </SimpleDialog>
}

function LiveVideo({videoRequestState}: RequestDialog) {
  const open= videoRequestState.state.liveVideoOpen;
  const setOpen= (liveVideoOpen: boolean) => videoRequestState.setState({...videoRequestState.state, liveVideoOpen});

  const videoOptions = useMemo(() => ({
    controls: false,
    autoplay: true,
    muted: true,
    fluid: true,
    sources: [{
      src: videoRequestState.state.hls,
      type: 'application/x-mpegURL'
    }]
  }), [videoRequestState.state.hls])

  return <SimpleDialog
    title="Live Video"
    open={open}
    onCancel={() => setOpen(false)}
    sx={{".MuiDialogContent-root": {overflowY: "visible"}}}
  >
    {videoRequestState.state.hls ? <VideoJS options={videoOptions} /> : <>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
        <CircularProgress />
      </Box>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", pt: 2}}>
        Connecting to the Camera..
      </Box>
      <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
        <i>Please be patient. This can take up to one minute.</i>
      </Box>
    </>}
  </SimpleDialog>
}

function RequestHistoricVideo({videoRequestState}: RequestDialog) {
  const open= videoRequestState.state.requestHistoricVideoOpen;
  const setOpen= (requestHistoricVideoOpen: boolean) => videoRequestState.setState({...videoRequestState.state, requestHistoricVideoOpen});
  const {device} = videoRequestState.state;

  const now = dayjs();

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const [loading, setLoading] = useState(false);
  const [fromTime, setFromTime] = useState(now.subtract(60, "seconds").format());
  const [toTime, setToTime] = useState(now.format());
  const [camera, setCamera] = useState(0);

  return <SimpleDialog
    open={open}
    onCancel={() => setOpen(false)}
    title={`Request Video for ${device?.deviceType}`}
    onSubmit={() => {
      setLoading(true);

      axios.post(endpoints.cameras.requestHistoricVideo, JSON.stringify({deviceId: device!.id, fromTime, toTime})).then(response => {
        enqueueSnackbar("Historic video has been requested. If the video is available you will be able to access it in the Video notifications page.", {autoHideDuration: 5000});
      }).catch(() => {
        enqueueSnackbar("An unexpected error has occured.", {variant: "error"});
      }).finally(() => {
        setLoading(false);
      })
    }}
    submitDisabled={loading}
    submitButton="Request Historic Video"
    submitButtonIcon={<Iconify icon="qlementine-icons:camera-16" />}
    submitButtonColor="info"
    sx={{".MuiDialogContent-root": {overflowY: "visible"}}}
  >
    <Stack direction="column" gap={1}>
      <Alert icon={<Box sx={{display:"flex", alignItems: "center", justifyContent: "center"}}><Iconify icon="qlementine-icons:camera-16" /></Box>} severity="success" sx={{mb: 2}}>
        After you request a video you will be able to view it on the Camera Videos page.
      </Alert>

      <TruxFormField label="Camera">
        <TruxSimpleSelectField value={camera} onChange={setCamera} disabled>
          <MenuItem value={0}>Front Camera</MenuItem>
          <MenuItem value={1}>Back Camera (Driver)</MenuItem>
        </TruxSimpleSelectField>
      </TruxFormField>
      <TruxFormField label="From Time">
        <TruxSimpleDateTimeField limitWidth={false} value={fromTime} onChange={setFromTime} timezone={timezone} />
      </TruxFormField>
      <TruxFormField label="To Time">
        <TruxSimpleDateTimeField limitWidth={false} value={toTime} onChange={setToTime} timezone={timezone} />
      </TruxFormField>
    </Stack>
  </SimpleDialog>
}

function DeviceMoreButton({
  updateData,
  processDevicesUpdateResponse,
  onEldEngineImmobilizerUpdated,
  eldEngineImmobilizer,
  setSelectedVehicles,
  setShareLocationDialogOpen,
  device,
  setDeviceForHistory,
  setViewingMode,
  mapRef,
}: {
  processDevicesUpdateResponse: (a: any) => void,
  updateData: any,
  onEldEngineImmobilizerUpdated: (a: EldEngineImmobilizer) => void,
  eldEngineImmobilizer?: EldEngineImmobilizer,
  setSelectedVehicles: (vehicles: DeviceWithMapInfo[]) => void,
  setShareLocationDialogOpen: (open: boolean) => void,
  device: DeviceWithMapInfo,
  setDeviceForHistory: (device: DeviceWithMapInfo) => void,
  setViewingMode: (mode: "sidebar" | "history") => void,
  mapRef: React.MutableRefObject<google.maps.Map | null>,
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [loading, setLoading] = useState(false);

  const showSpinner = useMemo(() => {
    if (device.externalIdentifier.startsWith("5|")) {
      if (!eldEngineImmobilizer) {
        return false;
      }
      if (eldEngineImmobilizer.whereRequestedAt) {
        if (!eldEngineImmobilizer.whereLoadedAt) {
          if (dayjs(eldEngineImmobilizer.whereRequestedAt).isAfter(dayjs().add(-10, 'minutes'))) {
            return true;
          }

          return false;
        }

        if (dayjs(eldEngineImmobilizer.whereLoadedAt).isAfter(dayjs(eldEngineImmobilizer.whereRequestedAt)) || dayjs(eldEngineImmobilizer.statusLoadedAt).isAfter(dayjs(eldEngineImmobilizer.whereRequestedAt))) {
          return false;
        }

        if (dayjs(eldEngineImmobilizer.whereRequestedAt).isAfter(dayjs().add(-3, 'minutes'))) {
          if (dayjs(eldEngineImmobilizer.whereLoadedAt).isBefore(dayjs(eldEngineImmobilizer.whereRequestedAt)) || dayjs(eldEngineImmobilizer.statusLoadedAt).isBefore(dayjs(eldEngineImmobilizer.whereRequestedAt))) {
            return true;
          }
        }
      }
    }
    return false;
  }, [device, eldEngineImmobilizer]);

  return <>
    <IconButton disabled={loading || showSpinner} onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();

      if (device.externalIdentifier.startsWith("5|")) {
        if (!eldEngineImmobilizer) {
          return;
        }
        if (eldEngineImmobilizer.whereRequestedAt) {
          if (!eldEngineImmobilizer.whereLoadedAt) {
            return;
          }

          if (dayjs(eldEngineImmobilizer.whereRequestedAt).isAfter(dayjs().add(-3, 'minutes')) && dayjs(eldEngineImmobilizer.whereLoadedAt).isBefore(dayjs(eldEngineImmobilizer.whereRequestedAt))) {
            return;
          }
        }
      }
      handleClick(e);
    }}>
      {loading || showSpinner ? <CircularProgress size={15} /> : <Iconify icon="solar:menu-dots-bold" rotate={45} />}
    </IconButton>
    {open && <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
    >
      <MenuItem onClick={(e) => {
        setSelectedVehicles([device]);
        setShareLocationDialogOpen(true);
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }}>Share Location</MenuItem>

      <MenuItem onClick={(e) => {
        setViewingMode("history");
        setDeviceForHistory(device);
        e.preventDefault();
        e.stopPropagation();
        handleClose();
        const targetPosition = {
          lat: Number(device.lastPosLat),
          lng: Number(device.lastPosLong)
        };

        if (mapRef.current) {
          mapRef.current.panTo(targetPosition);
          if ((mapRef.current.getZoom() || 100) < 12) {
            mapRef.current.setZoom(12);
          }
        }
      }}>Device History</MenuItem>
      {/* 87 is SPG - they use old immobilizers not setup for realtime location reporting */}
      {device.organizationId === 87 && device.externalIdentifier.startsWith("5|") && <MenuItem onClick={(e) => {
        setLoading(true);
        axios.post(endpoints.eldEngineImmobilizer.requestUpdate, {
          deviceId: device.id,
        }).then((response) => {
          onEldEngineImmobilizerUpdated(response.data.eldEngineImmobilizer as any)

          async function keepLoadingData(id: number, executedTimes: number) {
            if (executedTimes >= 18) {
              return;
            }
            let done = false;
            try {
              const eldEngineImmobilizers: EldEngineImmobilizer[] = await axios.get(endpoints.eldEngineImmobilizer.findAll).then(r => r.data);

              const eldEngineImmobilizerFound = eldEngineImmobilizers.find(l => l.id === id);

              if (eldEngineImmobilizerFound) {
                updateData("eldEngineImmobilizers")(eldEngineImmobilizers)
                if (eldEngineImmobilizerFound.whereLoadedAt) {
                  if (eldEngineImmobilizerFound.whereLoadedAt != null && dayjs(eldEngineImmobilizerFound.whereLoadedAt).isAfter(dayjs(eldEngineImmobilizerFound.whereRequestedAt))) {
                    if (eldEngineImmobilizerFound.statusLoadedAt != null && dayjs(eldEngineImmobilizerFound.statusLoadedAt).isAfter(dayjs(eldEngineImmobilizerFound.whereRequestedAt))) {
                      done = true;
                      axios.get(endpoints.devices.findAll).then(processDevicesUpdateResponse);
                    }
                  }
                }
              }
            } finally {
              if (!done) {
                setTimeout(() => {
                  keepLoadingData(id, executedTimes+1);
                }, 10000);
              }
            }
          }

          keepLoadingData(response.data.eldEngineImmobilizer.id, 0);

          enqueueSnackbar("Successfully sent a location request to device. Please wait 30-45sec to get the latest location.");
        }).catch((error) => {
          if (error?.response?.status === 400) {
            enqueueSnackbar("There was an error processing the request.", {variant: "error"});
          } else {
            enqueueSnackbar("An unexpected error has occured. Please check your internet connection and try again.", { variant: "error" });
          }
        }).finally(() => {
          setLoading(false);
        })
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }}>Load Status & Location</MenuItem>}
      {device.externalIdentifier.startsWith("5|") &&
        <>
          <MenuItem onClick={(e) => {
            if (!eldEngineImmobilizer) {
              return;
            }
            setLoading(true);

            axios.post(endpoints.eldEngineImmobilizer.togglePower, {
              deviceId: device.id,
              relayOn: false,
            }).then(() => {
              enqueueSnackbar("Successfully initiated a Power Toggle Request. Please wait 30-45sec and request the Status and the Location again.")
            }).catch((error) => {
              if (error.response?.status === 403) {
                enqueueSnackbar("Please wait for the location to be loaded before Powering the Immobilizer on/off", { variant: "error" });
              } else {
                enqueueSnackbar("An unexpected error has occured. Please check your internet connection and try again.", { variant: "error" })
              }
            }).finally(() => {
              setLoading(false);
            })
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }}>Power Off</MenuItem>
            <MenuItem onClick={(e) => {
              if (!eldEngineImmobilizer) {
                return;
              }
              setLoading(true);

              axios.post(endpoints.eldEngineImmobilizer.togglePower, {
                deviceId: device.id,
                relayOn: true,
              }).then(() => {
                enqueueSnackbar("Successfully initiated a Power Toggle Request. Please wait 30-45sec and request the Status and the Location again.")
              }).catch((error) => {
                if (error.response?.status === 403) {
                  enqueueSnackbar("Please wait for the location to be loaded before Powering the Immobilizer on/off", { variant: "error" });
                } else {
                  enqueueSnackbar("An unexpected error has occured. Please check your internet connection and try again.", { variant: "error" })
                }
              }).finally(() => {
                setLoading(false);
              })
              e.preventDefault();
              e.stopPropagation();
              handleClose();
            }}>Power On</MenuItem>
        </>
      }
    </Menu>}
  </>
}

// Main Google Maps component
function DeviceMapGoogle({
  deviceEldDrivers,
  handleShareLocationClick,
  selectedVehicles,
  setSelectedVehicles,
  expirationDate,
  setExpirationDate,
  shareLocationDialogOpen,
  setShareLocationDialogOpen,
  data,
  filteredDevices,
  selectedUuid,
  deviceHistory,
  setDeviceHistory,
  setSelectedMarker,
  timezone,
  processDevicesUpdateResponse,
  mapRef,
  onTruckMarkerClick,
  viewingMode,
  setViewingMode,
  deviceForHistory,
  setDeviceForHistory,
  dateForHistory,
  setDateForHistory,
  mapOptionsOpen,
  setMapOptionsOpen,
  mapType,
  setMapType,
  selectedOptions,
  setSelectedOptions,
  tileFilters,
  handleTileToggle,
  devicesPerTileFilter,
}: {
  deviceEldDrivers: DeviceEldDriver[],
  handleShareLocationClick: (vehicle?: DeviceWithMapInfo) => void;
  selectedVehicles: DeviceWithMapInfo[];
  setSelectedVehicles: (vehicles: DeviceWithMapInfo[]) => void;
  expirationDate: string;
  setExpirationDate: (date: string) => void;
  shareLocationDialogOpen: boolean;
  setShareLocationDialogOpen: (open: boolean) => void;
  data: PageModel,
  filteredDevices: DeviceWithMapInfo[],
  selectedUuid: string,
  deviceHistory: DeviceHistoryLineWithCityNameGroup[];
  setDeviceHistory: (history: DeviceHistoryLineWithCityNameGroup[]) => void,
  setSelectedMarker: (index: number) => void,
  timezone: string,
  processDevicesUpdateResponse: (response: any) => void,
  mapRef: React.MutableRefObject<google.maps.Map | null>,
  onTruckMarkerClick: (device: DeviceWithMapInfo, drivers: DeviceEldDriver[]) => void,
  viewingMode: "sidebar" | "history",
  setViewingMode: (mode: "sidebar" | "history") => void,
  deviceForHistory: DeviceWithMapInfo | undefined,
  setDeviceForHistory: (device: DeviceWithMapInfo | undefined) => void,
  dateForHistory: string,
  setDateForHistory: (date: string) => void,
  mapOptionsOpen: HTMLElement | null,
  setMapOptionsOpen: (element: HTMLElement | null) => void,
  mapType: 'default' | 'satellite' | 'terrain',
  setMapType: (type: 'default' | 'satellite' | 'terrain') => void,
  selectedOptions: string[],
  setSelectedOptions: (options: string[]) => void,
  tileFilters: Set<string>,
  handleTileToggle: (tileId: string) => void,
  devicesPerTileFilter: Record<string, number[]>,
}) {
  const [openInfoWindowUuid, setOpenInfoWindowUuid] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const selectedUuidPrev = usePrevious(selectedUuid);

  const [selectedHistoryPoint, setSelectedHistoryPoint] = useState<DeviceHistoryLineWithCityNameGroup | null>(null);

  const [shareLinkMultiple, setShareLinkMultiple] = useState<string>("");
  const [shareLinkLoading, setShareLinkLoading] = useState<boolean>(false);
  const context = useAuthContext();
  const { session } = context;

  // Prepare marker data
  const markers: MarkerData[] = useMemo(() => filteredDevices
      .filter(device => device.lastPosLat && device.lastPosLong && device.lastPing)
      .map(device => ({
        device,
        position: {
          lat: Number(device.lastPosLat),
          lng: Number(device.lastPosLong)
        }
      })), [filteredDevices]);

  // Open info window for selected device
  useEffect(() => {
    if (selectedUuid && selectedUuid !== selectedUuidPrev) {
      setOpenInfoWindowUuid(selectedUuid);
      const marker = markers.find(m => m.device.deviceUuid === selectedUuid);
      if (marker && mapRef.current) {
        mapRef.current.panTo(marker.position);
        if ((mapRef.current.getZoom() || 100) < 12) {
          mapRef.current.setZoom(12);
        }
      }
    }
  }, [selectedUuid, markers, mapRef, selectedUuidPrev]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const onToggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }, []);

  const handleGenerateShareLink = useCallback(async () => {
    if (selectedVehicles.length === 0) {
      enqueueSnackbar("Please select at least one vehicle", { variant: "error" });
      return;
    }

    setShareLinkLoading(true);
    await axios.post(endpoints.devices.createShareLinkMultiple, {
      deviceIds: selectedVehicles.map(v => v.id),
      organizationId: session?.organization?.id,
      expirationDate,
      // showOrganizationName: showOrganizationName,
    }).then((response) => {
      setShareLinkMultiple(response.data.shareLink);
    }).catch(() => {
      enqueueSnackbar("Failed to generate share link", { variant: "error" });
    }).finally(() => {
      setShareLinkLoading(false);
    });
  }, [selectedVehicles, expirationDate, session?.organization.id]);

  const handleCopyLink = useCallback(async () => {
    try {
      enqueueSnackbar("Share link copied to clipboard.");
      await navigator.clipboard.writeText(window.location.origin + shareLinkMultiple);
    } catch (error) {
      enqueueSnackbar("Failed to copy link.", { variant: "error" });
    }
  }, [shareLinkMultiple]);

  return (
    <>
      <Box sx={{ flexGrow: 1, position: "relative" }}>
        {/* Refresh button */}
        {/* <Box sx={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: "1000",
          background: "white",
          borderRadius: "4px",
          boxShadow: "0 0 10px rgba(0,0,0,0.2)",
          display: "flex",
          flexDirection: "row",
        }}>
          <Button variant="outlined" sx={{ fontWeight: "300" }} onClick={() => {
            if (loadingRef.current) return;
            loadingRef.current = true;
            axios.get(endpoints.devices.findAll).then(processDevicesUpdateResponse).finally(() => {
              loadingRef.current = false;
            });
          }}>
            <Iconify icon="solar:refresh-outline" />
          </Button>
        </Box> */}

        {!context?.session?.organization?.deviceOnly && <VehiclesTileFilter
          tileFilters={tileFilters}
          onTileToggle={handleTileToggle}
          devicesPerTileFilter={devicesPerTileFilter}
        />}

        <Box sx={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: "1000",
          borderRadius: "4px",
          display: "flex",
          flexDirection: "column",
          alignItems: "end",
          gap: "10px"
        }}>
          <Button variant="contained" sx={{ background: "#FEFEFE", color: "black", "&:hover": {background: "#DEDEDE"}, fontWeight: "400", boxShadow: "0 0 10px rgba(0,0,0,0.2)" }} onClick={() => handleShareLocationClick(undefined)}>
            <Iconify icon="ic:baseline-share" sx={{marginRight: "5px"}} /> Share Location
          </Button>

          <Box>
             <Tooltip title="Map Options" arrow>
               <Button
                 className="map-overlay-element"
                 variant="contained"
                 sx={{minWidth: "initial", background: "#FEFEFE", color: "black", "&:hover": {background: "#DEDEDE"}, boxShadow: "0 0 10px rgba(0,0,0,0.2)" }}
                 onClick={(e) => setMapOptionsOpen(e.currentTarget)}
               >
                 <Iconify icon="material-symbols-light:layers-outline" />
               </Button>
             </Tooltip>
           </Box>

          <Box>
            <Tooltip title="Toggle fullscreen" arrow>
              <Button
                className="map-overlay-element"
                variant="contained"
                onClick={onToggleFullScreen}
                sx={{minWidth: "initial", background: "#FEFEFE", color: "black", "&:hover": {background: "#DEDEDE"}, boxShadow: "0 0 10px rgba(0,0,0,0.2)" }}
              >
                <Iconify icon={isFullscreen ? "material-symbols-light:fullscreen-exit" : "material-symbols-light:fullscreen"} />
              </Button>
            </Tooltip>
          </Box>
        </Box>


        {/* Zoom controls */}
        <Box sx={{
          position: "absolute",
          top: "20px",
          left: "20px",
          zIndex: "1000",
          background: "white",
          borderRadius: "3px",
          boxShadow: "0 0 10px rgba(0,0,0,0.2)",
          border: "1px solid rgba(145, 158, 171, 0.32)",
          display: "flex",
          flexDirection: "column",
        }}>
          <IconButton size="small" onClick={() => {
            if (mapRef.current) {
              const currentZoom = mapRef.current.getZoom();
              if (currentZoom !== undefined) {
                mapRef.current.setZoom(currentZoom + 1);
              }
            }
          }}>
            <Iconify icon="mdi:plus" />
          </IconButton>
          <Box sx={{ borderTop: "1px solid #e0e0e0" }} />
          <IconButton size="small" onClick={() => {
            if (mapRef.current) {
              const currentZoom = mapRef.current.getZoom();
              if (currentZoom !== undefined) {
                mapRef.current.setZoom(currentZoom - 1);
              }
            }
          }}>
            <Iconify icon="mdi:minus" />
          </IconButton>
        </Box>

        {/* Map */}
        <GoogleMapWrapper
          mapType={mapType}
          mapRef={mapRef}
        >
          {/* Landmarks layer */}
          {selectedOptions.includes("landmarks") && (
            <LandmarksLayer landmarks={data.eldLandmarks} isGeofence={false} />
          )}

          {selectedOptions.includes("geofences") && (
            <LandmarksLayer landmarks={data.eldLandmarks} isGeofence />
          )}

          {/* Weigh stations layer */}
          {selectedOptions.includes("weighStation") && (
            <WeighStationsLayer />
          )}

          {selectedOptions.includes("traffic") && (
            <TrafficLayer />
          )}

          {/* Device markers with clustering */}
          <MarkerClustererComponent
            deviceEldDrivers={deviceEldDrivers}
            markersData={deviceForHistory ? markers.filter(marker => marker.device.id === deviceForHistory.id) : markers}
            openInfoWindowUuid={openInfoWindowUuid}
            setOpenInfoWindowUuid={setOpenInfoWindowUuid}
            timezone={timezone}
            eldConnectedVehicleUnitNumbers={data.eldConnectedVehicleUnitNumbers}
            onTruckMarkerClick={onTruckMarkerClick}
          />

          {/* Device history path overlay */}
          {deviceHistory && deviceHistory.length > 0 && (
            <DeviceHistoryPath deviceHistory={deviceHistory} selectedHistoryPoint={selectedHistoryPoint} setSelectedHistoryPoint={setSelectedHistoryPoint} timezone={timezone} setOpenInfoWindowUuid={setOpenInfoWindowUuid} />
          )}
        </GoogleMapWrapper>

        {/* Device History Bar */}
        {viewingMode === "history" && (
          <DeviceHistoryBar deviceForHistory={deviceForHistory} deviceHistory={deviceHistory} setDeviceHistory={setDeviceHistory} setDeviceForHistory={setDeviceForHistory} setViewingMode={setViewingMode} timezone={timezone} mapRef={mapRef} dateForHistory={dateForHistory} setDateForHistory={setDateForHistory} setSelectedHistoryPoint={setSelectedHistoryPoint} />
        )}
      </Box>

      {/* Share Location Dialog */}
      <SimpleDialog
        title="Share Location"
        open={shareLocationDialogOpen}
        onCancel={() => {
          setShareLinkMultiple("");
          setSelectedVehicles([]);
          if (!shareLinkMultiple) {
            setShareLocationDialogOpen(false);
          }
        }}
        submitButton={shareLinkLoading ? "Generating..." : "Generate Share Link"}
        submitDisabled={shareLinkLoading}
        submitButtonIcon={shareLinkLoading ? <CircularProgress size={16} /> : <Iconify icon="solar:link-bold" />}
        submitButtonColor="success"
        onSubmit={shareLinkMultiple ? undefined : handleGenerateShareLink}
        size="medium"
      >
        {shareLinkMultiple ? (
          <TruxFormField label="Share Link">
            <Box display="flex" gap={1}>
              <Input
                fullWidth
                value={window.location.origin + shareLinkMultiple}
                readOnly
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyLink}>
                      <Iconify icon="solar:copy-linear" />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>
          </TruxFormField>
        ) : (
        <Stack direction="column" gap={2}>
          <TruxFormField label="Vehicles">
            <Autocomplete multiple disableCloseOnSelect options={filteredDevices} value={selectedVehicles}
              onChange={(_event, newValue) => setSelectedVehicles(newValue)}
              getOptionLabel={(option) => option.deviceType}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select vehicles" />
              )}
              ChipProps={{ sx: { height: "27px", backgroundColor: Colors.lightGreen, color: Colors.greyText, "&:hover": { backgroundColor: Colors.lightGreen, color: Colors.greyText }} }}
            />
          </TruxFormField>

          <TruxFormField label="Expiration Date">
            <TruxSimpleDateField value={expirationDate} onChange={(d) => setExpirationDate(d!)} limitWidth={false}/>
          </TruxFormField>

        </Stack>)}
      </SimpleDialog>

      {/* Map Options Menu */}
      <Popover
        open={Boolean(mapOptionsOpen)}
        anchorEl={mapOptionsOpen}
        onClose={() => setMapOptionsOpen(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            maxWidth: 320,
            p: 2,
            borderRadius: 0.8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: "3px"
          }
        }}
      >
        <Stack spacing={2}>
          {/* Map Type Selection */}
          <FormControl component="fieldset">
            <FormLabel sx={{
              fontWeight: 600,
              color: 'text.secondary',
              mb: 1,
              '&.Mui-focused': {
                color: 'text.secondary'
              },
              '&.MuiFormLabel-filled': {
                color: 'text.secondary'
              }
            }}>
              MAP BASE
            </FormLabel>
              <RadioGroup
                value={mapType}
                onChange={(e) => setMapType(e.target.value as 'default' | 'satellite' | 'terrain')}
                sx={{ paddingLeft: "2px" }}
              >
                {MAP_TYPES.map((type) => (
                  <FormControlLabel
                    key={type.key}
                    value={type.key}
                    control={<Radio size="small" />}
                    label={type.label}
                  />
                ))}
              </RadioGroup>
          </FormControl>

          <Divider />

          {/* Overlay Options */}
          <FormControl component="fieldset">
            <FormLabel sx={{
              fontWeight: 600,
              color: 'text.secondary',
              mb: 2,
              '&.Mui-focused': {
                color: 'text.secondary'
              },
              '&.MuiFormLabel-filled': {
                color: 'text.secondary'
              }
            }}>
              OVERLAYS
            </FormLabel>
            <Stack spacing={2} paddingLeft="8px">
              {MAP_OVERLAYS.map((overlay) => (
                <FormControlLabel
                  key={overlay.key}
                  control={
                    <Checkbox
                      size="small"
                      sx={{ paddingX: "6px" }}
                      checked={selectedOptions.includes(overlay.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedOptions([...selectedOptions, overlay.key]);
                        } else {
                          setSelectedOptions(selectedOptions.filter(option => option !== overlay.key));
                        }
                      }}
                    />
                  }
                  label={overlay.label}
                />
              ))}
            </Stack>
          </FormControl>
        </Stack>
      </Popover>
    </>
  );
}

type TileBoxProps = {
  tileId: string;
  isActive: boolean;
  onToggle: (tileId: string) => void;
  count: number;
  iconifyType?: {icon: string, color?: string};
  statusType?: {eventType: string, color: string};
};

const TileBox = ({
  tileId,
  isActive,
  onToggle,
  count,
  iconifyType,
  statusType,
}: TileBoxProps) => (
  <Box sx={{
    height: "60px",
    width: "44px",
    padding: "4px",
  }}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "50px",
        justifyContent: "center",
        backgroundColor: isActive ? "#d0d0d0" : "transparent",
        borderRadius: "4px",
        cursor: "pointer",
        transition: "background-color 0.2s",
        "&:hover": { backgroundColor: "#e0e0e0" },
        "& .MuiIconButton-root": {
          "&:hover": {
            backgroundColor: "transparent !important"
          },
          "&:focus": {
            backgroundColor: "transparent !important"
          },
          "&:active": {
            backgroundColor: "transparent !important"
          }
        }
      }}
      onClick={() => onToggle(tileId)}
    >
      {iconifyType &&
      <IconButton sx={{ fontWeight: "400", width: "32px", height: "32px", padding: "8px 0 0 0" }}>
        <Iconify icon={iconifyType.icon} {...(iconifyType.color && {sx: {color: iconifyType.color}})}/>
      </IconButton>}
      {statusType &&
        <Button variant="contained" sx={{color:"#fff", fontSize:"14px", fontWeight:"600", backgroundColor: statusType.color, marginTop:"4px", height: "28px", width: "30px", minWidth: 0, "&:hover": {backgroundColor: statusType.color}}}>
          {statusType.eventType}
        </Button>}
      <Typography color="GrayText" fontSize="12px">{count}</Typography>
    </Box>
  </Box>
);

type VehiclesTileFilterProps = {
  tileFilters: Set<string>;
  onTileToggle: (tileId: string) => void;
  devicesPerTileFilter: Record<string, number[]>;
};

function VehiclesTileFilter({ tileFilters, onTileToggle, devicesPerTileFilter }: VehiclesTileFilterProps) {

  return (
    <Stack
      className="map-overlay-element"
      divider={
        <Box sx={{width: "1px", height: "80%", backgroundColor: "#b5bbc2", borderRadius: "5px", marginX:"5px"}}/>
      }
      sx={{
        display: { xs: 'none', mapBreakpoint: 'flex'},
        position: "absolute",
        top: "20px",
        right: "calc(50% - 160px)",
        backgroundColor: "#fefefe",
        height: "60px",
        zIndex: "1000",
        borderRadius: "4px",
      }}
      direction="row"
      justifyContent="center"
      alignItems="center"
    >
      <Stack direction="row">
        <TileBox tileId="trailer" isActive={tileFilters.has("trailer")} onToggle={onTileToggle} count={devicesPerTileFilter.trailer.length} iconifyType={{icon: "clarity:trailer-solid"}} />
        <TileBox tileId="truck" isActive={tileFilters.has("truck")} onToggle={onTileToggle} count={devicesPerTileFilter.truck.length} iconifyType={{icon: "clarity:truck-solid"}} />
      </Stack>

      <Stack direction="row" justifyContent="center" alignItems="center">
        <TileBox tileId="driving" isActive={tileFilters.has("driving")} onToggle={onTileToggle} count={devicesPerTileFilter.driving.length} statusType={{eventType: "D", color: "#377e22"}} />
        <TileBox tileId="onDuty" isActive={tileFilters.has("onDuty")} onToggle={onTileToggle} count={devicesPerTileFilter.onDuty.length} statusType={{eventType: "ON", color: "#f2aa3b"}} />
        <TileBox tileId="offDuty" isActive={tileFilters.has("offDuty")} onToggle={onTileToggle} count={devicesPerTileFilter.offDuty.length} statusType={{eventType: "OFF", color: "#ea3423"}} />
        <TileBox tileId="sleeper" isActive={tileFilters.has("sleeper")} onToggle={onTileToggle} count={devicesPerTileFilter.sleeper.length} statusType={{eventType: "SB", color: "#000"}} />
      </Stack>

      <Stack direction="row">
        <TileBox tileId="connected" isActive={tileFilters.has("connected")} onToggle={onTileToggle} count={devicesPerTileFilter.connected.length} iconifyType={{icon: "ph:cell-signal-full", color: "#4CAF50"}} />
        <TileBox tileId="disconnected" isActive={tileFilters.has("disconnected")} onToggle={onTileToggle} count={devicesPerTileFilter.disconnected.length} iconifyType={{icon: "ph:cell-signal-slash", color: "#ff5631"}} />
      </Stack>
    </Stack>
  );
}

// Marker clusterer component
export function MarkerClustererComponent({
  deviceEldDrivers,
  markersData,
  openInfoWindowUuid,
  setOpenInfoWindowUuid,
  timezone,
  eldConnectedVehicleUnitNumbers,
  onTruckMarkerClick
}: {
  deviceEldDrivers: DeviceEldDriver[],
  markersData: MarkerData[],
  openInfoWindowUuid: string | null,
  setOpenInfoWindowUuid: (uuid: string | null) => void,
  timezone: string,
  eldConnectedVehicleUnitNumbers: string[],
  onTruckMarkerClick: (device: DeviceWithMapInfo, drivers: DeviceEldDriver[]) => void,
}) {
  const map = useMap();

  const clusterer = useMemo(() => {
    if (!map) return null;

    return new MarkerClusterer({
          map,
          markers: [],
          algorithmOptions: {
            maxZoom: 10, // Disable clustering at zoom 10+
            radius: 120,
          } as SuperClusterOptions,
          onClusterClick: (e, c) => {
            map.setCenter(c.position)
            if (c.bounds) {
              map.fitBounds(c.bounds, 40)
            }
          },
          renderer: new ClusterRenderer()
        })
  }, [map]);

  const [markers, setMarkers] = useState<{[key: string]: Marker}>({});

  const selectedMarker = useMemo(
    () =>
      markersData && openInfoWindowUuid
        ? markersData.find(t => t.device.deviceUuid === openInfoWindowUuid)!
        : null,
    [markersData, openInfoWindowUuid]
  );

  useEffect(() => {
    if (!clusterer) return;

    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers]);

  // this callback will effectively get passsed as ref to the markers to keep
  // tracks of markers currently on the map
  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    setMarkers(markersList => {
      if ((marker && markersList[key]) || (!marker && !markersList[key]))
        return markersList;

      if (marker) {
        return {...markersList, [key]: marker};
      }
        const {[key]: _, ...newMarkers} = markersList;

        return newMarkers;

    });
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setOpenInfoWindowUuid(null);
  }, [setOpenInfoWindowUuid]);

  const handleMarkerClick = useCallback((markerData: MarkerData) => {
    onTruckMarkerClick(markerData.device, getDeviceDrivers(deviceEldDrivers, markerData.device) || []);
    setOpenInfoWindowUuid(markerData.device.deviceUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTruckMarkerClick]);

  return (
    <>
      {markersData.map((markerData) => (
        <CustomMapMarker
          key={markerData.device.deviceUuid}
          markerData={markerData}
          setMarkerRef={setMarkerRef}
          onClick={handleMarkerClick}
        />
      ))}

      {openInfoWindowUuid && selectedMarker?.device && (
        <InfoWindow
          disableAutoPan
          headerContent={<DeviceInfoCardHeader device={selectedMarker.device} />}
          anchor={markers[openInfoWindowUuid]}
          pixelOffset={[0, -18]}
          shouldFocus={false}
          onCloseClick={handleInfoWindowClose}>
          <DeviceInfoCard deviceEldDrivers={deviceEldDrivers} device={selectedMarker.device} timezone={timezone} />
        </InfoWindow>
      )}
    </>
  );
}

function DeviceInfoCardHeader({ device }: {device: DeviceWithMapInfo }) {
  const isTruck = device.deviceType.startsWith("ELD Truck #");
  return <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0px' }}>
    <div style={{ display: 'flex', justifyContent: 'center', marginRight: '10px', width: '30px', textAlign: 'center' }}>
      {isTruck ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="24" viewBox="0 0 36 36">
          <path fill="currentColor" d="M30 12h-4V7a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h1V8h20v11.7a6.5 6.5 0 0 1 1.56-.2H26V14h4a2 2 0 0 1 2 2v1h-4v2h4v5h-2.4a4.54 4.54 0 0 0-8.34 0h-6.83a4.5 4.5 0 0 0-4.17-2.76A4.38 4.38 0 1 0 14.72 26H21a4.49 4.49 0 0 0 8.92 0H33a1 1 0 0 0 1-1v-9a4 4 0 0 0-4-4M10.26 28a2.38 2.38 0 1 1 0-4.75a2.38 2.38 0 1 1 0 4.75m15.17 0a2.38 2.38 0 1 1 2.5-2.37a2.44 2.44 0 0 1-2.5 2.37" />
          <path fill="none" d="M0 0h36v36H0z"/>
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" style={{ marginLeft: '2px' }} height="24" viewBox="0 0 36 36">
          <path fill="currentColor" d="M15 19.2c-3.2 0-5.8 2.6-5.8 5.8s2.6 5.8 5.8 5.8s5.8-2.6 5.8-5.8s-2.6-5.8-5.8-5.8m0 10c-2.3 0-4.2-1.9-4.2-4.2s1.9-4.2 4.2-4.2s4.2 1.9 4.2 4.2s-1.9 4.2-4.2 4.2" />
          <path fill="currentColor" d="M14 24h2v2h-2z" />
          <path fill="currentColor" d="M33 9H2v13.1C2 24.3 3.7 26 5.9 26H7v-2H5.9c-1 0-1.8-.8-1.9-1.9V15h22v7.1c0 1-.8 1.8-1.9 1.9H23v2h1.1c2.1 0 3.8-1.7 3.8-3.9V11h5c.6 0 1-.4 1-1s-.3-1-.9-1m-7 4H4v-2h22z" />
          <path fill="none" d="M0 0h36v36H0z"/>
        </svg>
      )}
    </div>
    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{device.deviceType}</div>
  </div>;
}

function DeviceInfoCard({ deviceEldDrivers, device, timezone }: {deviceEldDrivers: DeviceEldDriver[], device: DeviceWithMapInfo, timezone: string, }) {
  return (
    <div>
      {getDeviceDrivers(deviceEldDrivers, device).map((eldDriver, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginRight: '10px', width: '30px', textAlign: 'center' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24">
              <g fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="6" r="4"/>
                <path d="M20 17.5c0 2.485 0 4.5-8 4.5s-8-2.015-8-4.5S7.582 13 12 13s8 2.015 8 4.5Z"/>
              </g>
            </svg>
          </div>
          <div style={{ fontSize: '14px' }}>
            {eldDriver.firstName} {eldDriver.lastName}
          </div>
          <div style={{ marginLeft: '5px' }}>
            {eldDriver.connected ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256">
                <path fill="#4CAF50" d="M168 72v128a8 8 0 0 1-16 0V72a8 8 0 0 1 16 0m32-48a8 8 0 0 0-8 8v168a8 8 0 0 0 16 0V32a8 8 0 0 0-8-8m-80 80a8 8 0 0 0-8 8v88a8 8 0 0 0 16 0v-88a8 8 0 0 0-8-8m-40 40a8 8 0 0 0-8 8v48a8 8 0 0 0 16 0v-48a8 8 0 0 0-8-8m-40 40a8 8 0 0 0-8 8v8a8 8 0 0 0 16 0v-8a8 8 0 0 0-8-8"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256">
                <path fill="#ff5631" d="M88 152v48a8 8 0 0 1-16 0v-48a8 8 0 0 1 16 0m-48 32a8 8 0 0 0-8 8v8a8 8 0 0 0 16 0v-8a8 8 0 0 0-8-8m173.92 26.62l-160-176a8 8 0 1 0-11.84 10.76L112 122.29V200a8 8 0 0 0 16 0v-60.11l24 26.4V200a8 8 0 0 0 16 0v-16.11l34.08 37.49a8 8 0 1 0 11.84-10.76m-53.92-87a8 8 0 0 0 8-8V72a8 8 0 0 0-16 0v43.63a8 8 0 0 0 8 8Zm40 44a8 8 0 0 0 8-8V32a8 8 0 0 0-16 0v127.63a8 8 0 0 0 8 8Z"/>
              </svg>
            )}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '7px' }} />

      <hr style={{ border: 'none', height: '1px', backgroundColor: '#e0e0e0', margin: '7px 0' }}/>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '7px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginRight: '10px', width: '30px', textAlign: 'center' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <g fill="none" stroke="#777" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5">
              <path d="M12.56 20.82a.96.96 0 0 1-1.12 0C6.611 17.378 1.486 10.298 6.667 5.182A7.6 7.6 0 0 1 12 3c2 0 3.919.785 5.333 2.181c5.181 5.116.056 12.196-4.773 15.64"/>
              <path d="M12 12a2 2 0 1 0 0-4a2 2 0 0 0 0 4"/>
            </g>
          </svg>
        </div>
        <div style={{ fontSize: '14px', color: '#777' }}>
          {device.location && (
            <>
              {device.location}
              <br />
            </>
          )}
          <a href={`https://maps.google.com/?q=${device.lastPosLat},${device.lastPosLong}`}>
            {device.lastPosLat}, {device.lastPosLong}
          </a>
        </div>
      </div>

      <hr style={{ border: 'none', height: '1px', backgroundColor: '#e0e0e0', margin: '7px 0' }}/>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginRight: '10px', width: '30px', textAlign: 'center' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
            <g fill="none" stroke="#777" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </g>
          </svg>
        </div>
        <div style={{ fontSize: '14px', color: '#777' }}>
          Last Ping: {formatDatetimeForPreview ? formatDatetimeForPreview(device.lastPing, timezone) : device.lastPing}
        </div>
      </div>

      {device.externalIdentifier && !device.externalIdentifier.startsWith("2|") && (
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginRight: '10px', width: '30px', textAlign: 'center' }}>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="#4CAF50">
              <path d="M17 5.33C17 4.6 16.4 4 15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33V20.67C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33zM13 18h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V8h2v2z"/>
            </svg>
          </div>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#4CAF50' }}>{device.battery}%</div>
        </div>
      )}

      {(device?.velocity || 0) > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginRight: '10px', width: '30px', textAlign: 'center' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path
                fill={getVelocityColor ? getVelocityColor(device) : '#000'}
                d="M10.45 15.5q.6.6 1.55.588t1.4-.688L19 7l-8.4 5.6q-.675.45-.712 1.375t.562 1.525M12 4q1.475 0 2.838.412T17.4 5.65l-1.9 1.2q-.825-.425-1.712-.637T12 6Q8.675 6 6.337 8.338T4 14q0 1.05.288 2.075T5.1 18h13.8q.575-.95.838-1.975T20 13.9q0-.9-.213-1.75t-.637-1.65l1.2-1.9q.75 1.175 1.188 2.5T22 13.85t-.325 2.725t-1.025 2.475q-.275.45-.75.7t-1 .25H5.1q-.525 0-1-.25t-.75-.7q-.65-1.125-1-2.387T2 14q0-2.075.788-3.887t2.15-3.175t3.187-2.15T12 4m.175 7.825"
              />
            </svg>
          </div>
          <div
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: getVelocityColor ? getVelocityColor(device) : '#000'
            }}
          >
            {device.velocity!.toFixed(2)} mph
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '7px' }} />
    </div>
  );
};

export class ClusterRenderer implements Renderer {
  public render(
    { count, position }: Cluster,
    stats: ClusterStats,
    map: google.maps.Map
  ): Marker {
    // change color if this cluster has more markers than the mean cluster
    const color =
      count > Math.max(10, stats.clusters.markers.mean) ? "#003eff" : "#2e96ff";

    // create svg literal with fill color
    const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
<circle cx="120" cy="120" opacity=".9" r="70" />
<circle cx="120" cy="120" opacity=".3" r="90" />
<text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="60" font-weight="700" dominant-baseline="middle">${count}</text>
</svg>`;

    const title = `Cluster of ${count} markers`;
      // adjust zIndex to be above other markers
      const zIndex: number = Number(google.maps.Marker.MAX_ZINDEX) + count;

    if (MarkerUtils.isAdvancedMarkerAvailable(map)) {
      // create cluster SVG element
      const parser = new DOMParser();
      const svgEl = parser.parseFromString(
        svg,
        "image/svg+xml"
      ).documentElement;
      svgEl.setAttribute("transform", "translate(0 25)");

      const clusterOptions: google.maps.marker.AdvancedMarkerElementOptions = {
        map,
        position,
        zIndex,
        title,
        content: svgEl,
      };
      return new google.maps.marker.AdvancedMarkerElement(clusterOptions);
    }

    const clusterOptions: google.maps.MarkerOptions = {
      position,
      zIndex,
      title,
      icon: {
        url: `data:image/svg+xml;base64,${btoa(svg)}`,
        anchor: new google.maps.Point(25, 25),
      },
    };
    return new google.maps.Marker(clusterOptions);
  }
}

export type TreeMarkerProps = {
  markerData: MarkerData;
  onClick: (markerData: MarkerData) => void;
  setMarkerRef: (marker: Marker | null, key: string) => void;
};

function RenderElement({device}: {device: DeviceWithMapInfo}) {
  const squareConnected = <svg xmlns="http://www.w3.org/2000/svg" width="14px" height="14px" viewBox="0 0 56 56">
    <rect x="3" y="3" width="50" height="50" rx="6" fill="#36b746" stroke="white" strokeWidth="3"/>
  </svg>;

  const squareDisconnected = <svg xmlns="http://www.w3.org/2000/svg" width="14px" height="14px" viewBox="0 0 56 56">
    <rect x="3" y="3" width="50" height="50" rx="6" fill="#696868ff" stroke="white" strokeWidth="3"/>
  </svg>;

  const getArrowConnected = (direction: number) => (
    <svg viewBox="0 0 100 100" width="28px" height="28px" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.2"/>
        </filter>
      </defs>
      <path
        d="M50,10 L85,75 L50,55 L15,75 Z"
        fill="#4CAF50"
        stroke="#FFFFFF"
        strokeWidth="4"
        strokeLinejoin="round"
        filter="url(#shadow)"
        transform={`rotate(${direction || 0} 50 50)`}
      />
    </svg>
  );

  const getArrowDisconnected = (direction: number) => (
    <svg viewBox="0 0 100 100" width="28px" height="28px" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow2" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.2"/>
        </filter>
      </defs>
      <path
        d="M50,10 L85,75 L50,55 L15,75 Z"
        fill="#696868"
        stroke="#333333"
        strokeWidth="4"
        strokeLinejoin="round"
        filter="url(#shadow2)"
        transform={`rotate(${direction || 0} 50 50)`}
      />
    </svg>
  );
  const isConnected = !device.lastPing || dayjs(device.lastPing).isAfter(dayjs().add(-10, 'minutes'));

  // eslint-disable-next-line no-nested-ternary
  return device.direction !== null && device.direction !== undefined && device.direction > -1 && device.direction !== 0
                ? (isConnected ? getArrowConnected(device.direction) : getArrowDisconnected(device.direction))
                : (isConnected ? squareConnected : squareDisconnected);
}

export const CustomMapMarker = (props: TreeMarkerProps) => {
  const {markerData, onClick, setMarkerRef} = props;

  const handleClick = useCallback(() => onClick(markerData), [onClick, markerData]);
  const ref = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement) =>
      setMarkerRef(marker, markerData.device.deviceUuid),
    [setMarkerRef, markerData.device.deviceUuid]
  );

  const isArrowMarker = markerData.device.direction !== null && markerData.device.direction !== undefined && markerData.device.direction > -1 && markerData.device.direction !== 0;

  return (
    <AdvancedMarker position={markerData.position} anchorPoint={isArrowMarker ? ["14px", "14px"] : ["7px", "7px"]} ref={ref} onClick={handleClick}>
      <div style={{ position: 'relative' }}>
        <div style={{
          backgroundColor: '#1D1D1D',
          color: 'white',
          fontSize: '10px',
          fontWeight: '500',
          padding: '2px 6px',
          borderRadius: '4px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '2px'
        }}>
          {markerData.device.deviceType.startsWith("ELD Truck #") ? `${markerData.device.deviceType.replace("ELD Truck #", "")}` : `${markerData.device.deviceType}`}
        </div>
        <RenderElement device={markerData.device} />
      </div>
    </AdvancedMarker>
  );
};

// Helper function to fetch weigh station data from S3
async function getStationData() {
  try {
    const response = await fetch('https://accountingimageuploads.s3.us-west-2.amazonaws.com/ws.json');
    const text = await response.text();

    const stationData: { stationId: string; lat: string; lon: string }[] = [];
    const lines = JSON.parse(text);
    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      stationData.push({
        stationId: `${line.address}, ${line.city}, ${line.state} ${line.zip}`,
        lat: line.lat,
        lon: line.lon
      });
    }

    return stationData;
  } catch (error: any) {
    console.error('Error fetching weigh station data:', error.message);
    throw error;
  }
}

type LandmarksLayerProps = {
  landmarks: EldLandmark[] | undefined,
  isGeofence: boolean,
}

// Component to render landmarks (circles and polygons)
export function LandmarksLayer({ landmarks, isGeofence }: LandmarksLayerProps) {
  const map = useMap();
  const shapesRef = useRef<(google.maps.Circle | google.maps.Polygon)[]>([]);

  useEffect(() => {
    if (!map || !landmarks) return () => {};

    // Clear previous shapes
    shapesRef.current.forEach(shape => shape.setMap(null));
    shapesRef.current = [];

    for (let i = 0; i < landmarks.length; i += 1) {
      const landmark = landmarks[i];

      if (!isGeofence || landmark.name === "STRICT HOS") {
        const landmarkStrokeFill = "rgb(51, 136, 255)";
        const geofenceStrokeFill = "rgb(255, 22, 22)";

        // Draw circles
        for (let j = 0; j < landmark.circles.length; j+=1) {
          const circle = landmark.circles[j];
          const circleShape = new google.maps.Circle({
            map,
            center: { lat: circle.center[0], lng: circle.center[1] },
            radius: circle.radius,
            strokeColor: isGeofence ? geofenceStrokeFill : landmarkStrokeFill,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: isGeofence ? geofenceStrokeFill : landmarkStrokeFill,
            fillOpacity: 0.15,
          })
          shapesRef.current.push(circleShape);
        }

        for (let j = 0; j < landmark.polygons.length; j+=1) {
          const polygon = landmark.polygons[j];
          const paths = polygon.map(coord => ({ lat: coord[0], lng: coord[1] }));
          const polygonShape = new google.maps.Polygon({
            map,
            paths,
            strokeColor: isGeofence ? geofenceStrokeFill : landmarkStrokeFill,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: isGeofence ? geofenceStrokeFill : landmarkStrokeFill,
            fillOpacity: 0.15,
          })
          shapesRef.current.push(polygonShape);
        }
      }
    }

    // Cleanup function
    return () => {
      shapesRef.current.forEach(shape => shape.setMap(null));
      shapesRef.current = [];
    };
  }, [map, landmarks, isGeofence]);

  return null;
}

export function TrafficLayer() {
  const map = useMap();

  React.useEffect(() => {
    const layer = new google.maps.TrafficLayer();
    layer.setMap(map);
    return () => layer.setMap(null)
  }, [map]);

  return null;
}
// Component to render weigh station markers
export function WeighStationsLayer() {
  const [stations, setStations] = useState<{ stationId: string; lat: string; lon: string }[]>([]);
  const [openStationId, setOpenStationId] = useState<string | null>(null);

  useEffect(() => {
    getStationData().then(setStations).catch(console.error);
  }, []);

  return (
    <>
      {stations.map((station, index) => (
        <React.Fragment key={index}>
          <AdvancedMarker
            position={{ lat: Number(station.lat), lng: Number(station.lon) }}
            onClick={() => setOpenStationId(station.stationId)}
          >
            <div style={{
              width: '5px',
              height: '5px',
              borderRadius: '50%',
              backgroundColor: '#2196F3'
            }} />
          </AdvancedMarker>
          {openStationId === station.stationId && (
            <InfoWindow
              disableAutoPan
              style={{
                boxShadow: '0px 0px 3px #bbb',
              }}
              shouldFocus={false}
              position={{ lat: Number(station.lat), lng: Number(station.lon) }}
              onCloseClick={() => setOpenStationId(null)}
              headerDisabled
            >
              <div>{station.stationId}</div>
            </InfoWindow>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

// Device History Bar component
type DeviceHistoryBarProps = {
  deviceForHistory: DeviceWithMapInfo | undefined,
  deviceHistory: DeviceHistoryLineWithCityNameGroup[],
  setDeviceHistory: (history: DeviceHistoryLineWithCityNameGroup[]) => void,
  setDeviceForHistory: (device: DeviceWithMapInfo | undefined) => void,
  setViewingMode: (mode: "sidebar" | "history") => void // to x out of the bar
  timezone: string,
  mapRef: React.MutableRefObject<google.maps.Map | null>,
  dateForHistory: string,
  setDateForHistory: (date: string) => void,
  setSelectedHistoryPoint: (line: DeviceHistoryLineWithCityNameGroup | null) => void,
}
function DeviceHistoryBar({ deviceForHistory, deviceHistory, setDeviceHistory, setDeviceForHistory, setViewingMode, timezone, mapRef, dateForHistory, setDateForHistory, setSelectedHistoryPoint }: DeviceHistoryBarProps) {
  const deviceForHistoryPrev = usePrevious(deviceForHistory);
  const dateForHistoryPrev = usePrevious(dateForHistory);

  const [historicLoading, setHistoricLoading] = useState(false);
  const currentHistoryRef = useRef<DeviceHistoryLineWithCityNameGroup[] | null>(null);

  const loadDeviceHistory = useCallback(async (showLoading = true) => {
    if (!deviceForHistory) return;

    if (showLoading) {
      setHistoricLoading(true);
    }

    await axios.get(`${formatEndpoint(
      endpoints.devices.findAllHistory,
      {
        eldDriverId: 0,
        date: dateForHistory
      }
    )}?deviceId=${deviceForHistory.id}`).then((response) => {
      if (response.data) {
        const deviceHistories: DeviceHistoryLineWithCityNameGroup[] = [];

        response.data.lines.forEach((line: any) => {
          const closeIndex = deviceHistories.findIndex(
            (existing) => isTheSame(existing.posLat, existing.posLong, line.posLat, line.posLong)
          );
          if (closeIndex >= 0) {
            deviceHistories[closeIndex].lines.push(line);
          } else {
            deviceHistories.push({
              ...line,
              lines: [],
            });
          }
        });

        // Only update if there are differences
        const prevHistory = currentHistoryRef.current;
        const hasChanges = !prevHistory || prevHistory.length !== deviceHistories.length ||
          prevHistory.some((prevLine: DeviceHistoryLineWithCityNameGroup, index: number) => {
            const newLine = deviceHistories[index];
            return !newLine ||
              prevLine.recordedAt !== newLine.recordedAt ||
              prevLine.posLat !== newLine.posLat ||
              prevLine.posLong !== newLine.posLong ||
              prevLine.lines.length !== newLine.lines.length;
          });

        if (hasChanges) {
          currentHistoryRef.current = deviceHistories;
          setDeviceHistory(deviceHistories);
          if (showLoading && mapRef.current && deviceHistories.length > 3) {
            const points = deviceHistories.map(({posLat, posLong}) => ({
              lat: Number(posLat),
              lng: Number(posLong),
            }))
            .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));

            if (points.length) {
              const bounds = new google.maps.LatLngBounds();
              points.forEach(p => bounds.extend(p));
              mapRef.current.fitBounds(bounds, 40);
            }
          }
        }
      }

      // we wont show loading for background history updates
      if (showLoading) {
        setHistoricLoading(false);
      }
    }).catch(() => {
      if (showLoading) {
        setHistoricLoading(false);
        enqueueSnackbar("Unexpected exception has occured. Please contact customer support.", {variant: "error"})
      }
    })
  }, [deviceForHistory, dateForHistory, setDeviceHistory, mapRef]);

  useEffect(() => {
    if (deviceForHistory && (deviceForHistory !== deviceForHistoryPrev || dateForHistory !== dateForHistoryPrev)) {
      loadDeviceHistory();
    }
  }, [deviceForHistory, dateForHistory, deviceForHistoryPrev, dateForHistoryPrev, loadDeviceHistory])

  useEffect(() => {
    if (useSockets) {
      return () => {};
    }
    if (!deviceForHistory) return () => {};

    const interval = setInterval(() => {
      loadDeviceHistory(false);
    }, 10_000);

    return () => {
      clearInterval(interval);
    };
  }, [deviceForHistory, loadDeviceHistory])

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: "20px",
        left: "50%",
        transform: "translateX(-50%)",
        width: deviceForHistory ? "90%" : "0px",
        height: "130px",
        backgroundColor: "white",
        borderRadius: "4px",
        boxShadow: "0 0 10px rgba(0,0,0,0.2)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {deviceForHistory && <>
      <Box sx={{position: "absolute", width: "30px", height: "30px", top: "-10px", right: "15px", backgroundColor: "white", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", border: "1px solid #e0e0e0"}}>
        <Tooltip title="Close">
          <IconButton
            size="small"
            onClick={() => {
              setViewingMode("sidebar");
              setDeviceForHistory(undefined);
              setDeviceHistory([]);
            }}
            sx={{
              width: "26px",
              height: "26px",
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                color: "#333"
              }
            }}
          >
            <Iconify icon="mdi:close" />
          </IconButton>
        </Tooltip>
      </Box>
      <Stack direction="column" gap={{sx: 0, md: 3}} alignItems="center" justifyContent="space-between" flexGrow={1} sx={{paddingLeft: "20px", paddingRight: "28px", maxWidth: "100%"}}>
        <Stack position="relative" direction={{sx: "column", md: "row"}} width="100%" alignItems="center" justifyContent="center" gap={{sx: 0, md: 2}}>
          {historicLoading ?
          <Box sx={{display: "flex", alignItems: "flex-start", justifyContent: "flex-start", gap: 1, height: "23px"}}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading device history...
            </Typography>
          </Box> : <>
            <Typography variant="body1" color="text.secondary" sx={{ paddingLeft:"8px", whiteSpace: "nowrap"}}>
              {deviceHistory.length > 0 ? `Location History - ${deviceForHistory.deviceType}` : `No Location History Found - ${deviceForHistory.deviceType}`}
            </Typography>
            <VehicleTimeline mapRef={mapRef} vehicleHistoryLines={deviceHistory} timezone={timezone} setSelectedHistoryPoint={setSelectedHistoryPoint} />
          </>}
        </Stack>
        <Stack direction="row" width="100%" alignItems="space-between" justifyContent="space-between">
          <Box width="230px" display="flex">
            <IconButton disabled={historicLoading} onClick={() => {
              setDateForHistory(DateTime.fromISO(dateForHistory).minus({days: 1}).toFormat("yyyy-MM-dd"));
            }}><Iconify icon="mdi:arrow-left" /></IconButton>
            <DatePicker disabled={historicLoading} value={dayjs(dateForHistory)}
              slotProps={{
                textField: {
                  disabled: true,
                  size: "small"
                }
              }}
              onChange={(d) => {
                if (d) {
                  setDateForHistory(d.format("YYYY-MM-DD"));
                }
              }}/>
            <IconButton disabled={historicLoading || DateTime.fromISO(dateForHistory) >= DateTime.now().setZone(timezone)} onClick={() => {
              setDateForHistory(DateTime.fromISO(dateForHistory).plus({days: 1}).toFormat("yyyy-MM-dd"));
            }}><Iconify icon="mdi:arrow-right" /></IconButton>
          </Box>
          <Tooltip title="Pan to current location">
            <Button size="small" color="info" variant="outlined" onClick={() => {
              if (!deviceForHistory || !mapRef.current) return;

              if (!deviceForHistory.lastPosLat || !deviceForHistory.lastPosLong) {
                enqueueSnackbar("Invalid latitude/longitude for device's current location.", { variant: "error" });
                return;
              }

              const targetPosition = {
                lat: Number(deviceForHistory.lastPosLat),
                lng: Number(deviceForHistory.lastPosLong)
              };

              mapRef.current.panTo(targetPosition);

              if ((mapRef.current.getZoom() || 100) < 12) {
                mapRef.current.setZoom(12);
              }
            }} sx={{
              display: "flex"
            }}>
              <Iconify icon="mdi:location" />
              <Box sx={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{deviceForHistory.location}</Box>
            </Button>
          </Tooltip>
        </Stack>
      </Stack>
      </>}
    </Box>
  );
}

type VehicleTimelineProps = {
  mapRef: React.MutableRefObject<google.maps.Map | null>,
  vehicleHistoryLines: DeviceHistoryLineWithCityNameGroup[],
  timezone: string,
  setSelectedHistoryPoint: (line: DeviceHistoryLineWithCityNameGroup | null) => void,
}

const VehicleTimeline = memo(({ mapRef, vehicleHistoryLines, timezone, setSelectedHistoryPoint }: VehicleTimelineProps) => {
  const stationaryColor = "#444444ff"
  const movingColor = "#288dff"

  return (
    <Box
      position="relative"
      width="100%"
      height={24}
      sx={{ marginTop: "6px", marginBottom: "6px", maxHeight: "11px"}}
    >
      <Box
        position="absolute"
        top="calc(50% - 1px)"
        left="0px"
        right="0px"
        height="2px"
        sx={{backgroundColor: "#eeeeee"}}
      />

      {vehicleHistoryLines.map((line, i) => {
        const recordedAt = DateTime.fromISO(line.recordedAt).setZone(timezone);
        const seconds = recordedAt.hour * 3600 + recordedAt.minute * 60 + recordedAt.second;
        const DAY = 24*60*60;
        const leftOffset = (seconds / DAY) * 100

        const vel = line.velocity ?? 0
        const color =
          vel > 5
            ? movingColor
            : stationaryColor

        const timeLabel = recordedAt.toFormat("hh:mm:ss a");

        let origin = "Samsara";
        if (line.lineOrigin !== 1) {
          if (line.lineOrigin === 2) {
            origin = "Eld Device";
          } else {
            origin = `${line.lineOrigin}`;
          }
        }

        return (
          <Tooltip key={i} arrow placement="bottom" color="green" title={
            <Box sx={{fontSize: "14px", padding: "4px"}}>
              <strong>{line.cityName}</strong>
              {line.lineOrigin && <>
                <br /><strong>{`Origin: ${origin}`}</strong>
              </>}
              {line.odometer && <>
                <br />{`Odometer: ${line.odometer.toFixed(2)}`}
              </>}
              <br />{`Time: ${timeLabel}`}
              <br />Speed: {vel.toFixed(0)} mph
            </Box>
          }>
            <Box
              position="absolute"
              top="50%"
              left={`${leftOffset}%`}
              width="8px"
              height="8px"
              bgcolor={color}
              borderRadius="50%"
              sx={{
                transform: 'translate(-50%, -50%)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  width:  "10px",
                  height: "10px",
                  boxShadow: '0 0 8px rgba(0,0,0,0.3)',
                }
              }}
              onClick={() => {
                setSelectedHistoryPoint(line);
                const targetPosition = {
                  lat: Number(line.posLat),
                  lng: Number(line.posLong),
                }

                if (mapRef.current && targetPosition.lat && targetPosition.lng) {
                  mapRef.current.panTo(targetPosition);
                  if ((mapRef.current.getZoom() || 100) < 12) {
                    mapRef.current.setZoom(12);
                  }
                }
              }}
            />
          </Tooltip>
        )
      })}
    </Box>
  )
})

function calculateDistance(
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number {
  const earthRadius = 6371.0; // Radius of the Earth in kilometers

  const dLat = toRadians(point2.lat - point1.lat);
  const dLon = toRadians(point2.lng - point1.lng);

  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(toRadians(point1.lat)) *
      Math.cos(toRadians(point2.lat)) *
      Math.sin(dLon/2) *
      Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = earthRadius * c;

  return distance * 0.621371; // Convert to miles
}

function toRadians(degree: number): number {
  return degree * Math.PI / 180;
}

function groupCoordinatesByDistance(
  coordinates: { lat: number; lng: number }[],
  maxDistanceMiles: number = 4 // 4 miles
): { lat: number; lng: number }[][] {
  if (coordinates.length < 2) return [coordinates];

  const segments: { lat: number; lng: number }[][] = [];
  let currentSegment: { lat: number; lng: number }[] = [coordinates[0]];

  for (let i = 1; i < coordinates.length; i += 1) {
    const prevPoint = coordinates[i - 1];
    const currentPoint = coordinates[i];
    const distance = calculateDistance(prevPoint, currentPoint);

    if (distance > maxDistanceMiles) {
      // end current segment
      segments.push(currentSegment);
      currentSegment = [currentPoint];
    } else {
      // add to current segment
      currentSegment.push(currentPoint);
    }
  }

  // Add the last segment
  if (currentSegment.length > 0) {
    segments.push(currentSegment);
  }

  return segments;
}

type DeviceHistoryPathProps = {
  deviceHistory: DeviceHistoryLineWithCityNameGroup[],
  selectedHistoryPoint: DeviceHistoryLineWithCityNameGroup | null,
  setSelectedHistoryPoint: (line: DeviceHistoryLineWithCityNameGroup | null) => void,
  timezone: string | undefined,
  setOpenInfoWindowUuid: (uuid: string | null) => void,
}

function DeviceHistoryPath({ deviceHistory, selectedHistoryPoint, setSelectedHistoryPoint, timezone, setOpenInfoWindowUuid }: DeviceHistoryPathProps) {
  const map = useMap();
  const pathRefs = useRef<google.maps.Polyline[]>([]);
  const tooltipRef = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const clickListener = map.addListener("click", () => {
      setSelectedHistoryPoint(null);
      if (tooltipRef.current) {
        tooltipRef.current.close();
      }
      tooltipRef.current = null;
    });

    return () => {
      clickListener.remove();
    };
  }, [map, setSelectedHistoryPoint]);

  useEffect(() => {
    if (!map || !deviceHistory || deviceHistory.length === 0) return;

    const pathCoordinates = deviceHistory
      .sort((a, b) => DateTime.fromISO(a.recordedAt).diff(DateTime.fromISO(b.recordedAt)).toMillis())
      .filter(line => line.posLat && line.posLong)
      .map(line => ({
        lat: Number(line.posLat),
        lng: Number(line.posLong)
      }));

    if (pathCoordinates.length < 2) return;

    if (selectedHistoryPoint && selectedHistoryPoint.posLat && selectedHistoryPoint.posLong) {
      setOpenInfoWindowUuid(null);

      if (deviceHistory.some(line => isTheSame(line.posLat, line.posLong, selectedHistoryPoint.posLat, selectedHistoryPoint.posLong))) {
        const vel = selectedHistoryPoint.velocity ?? 0

        let origin = "Samsara";
        if (selectedHistoryPoint.lineOrigin !== 1) {
          if (selectedHistoryPoint.lineOrigin === 2) {
            origin = "Eld Device";
          } else {
            origin = `${selectedHistoryPoint.lineOrigin}`;
          }
        }

        const body = document.createElement("div");
        body.style.fontSize = "14px";
        body.style.lineHeight = "1.4";
        body.style.textAlign = "left";
        body.style.boxShadow = '0px 0px 3px #bbb';

        body.innerHTML = `
          <strong>${selectedHistoryPoint.cityName}</strong><br />
          <a href="https://maps.google.com/?q=${selectedHistoryPoint.posLat},${selectedHistoryPoint.posLong}" target="_blank" rel="noopener noreferrer">
          ${selectedHistoryPoint.posLat}, ${selectedHistoryPoint.posLong}</a>
          ${selectedHistoryPoint.lineOrigin ? `<br /><strong>Origin: ${origin}</strong>` : ""}
          <!-- ${selectedHistoryPoint.odometer ? `<br />Odometer: ${selectedHistoryPoint.odometer.toFixed(2)}` : ""} -->
          <br />Time: ${DateTime.fromISO(selectedHistoryPoint.recordedAt).setZone(timezone || "America/Chicago").toFormat("hh:mm:ss a")}
          <br />Speed: ${vel.toFixed(0)} mph
        `;

        tooltipRef.current = new google.maps.InfoWindow({
          content: body,
          headerDisabled: true,
          position: {
            lat: Number(selectedHistoryPoint.posLat),
            lng: Number(selectedHistoryPoint.posLong),
          },
          zIndex: 1000,
          pixelOffset: {width: 0, height: -5} as google.maps.Size,
        });
        tooltipRef.current.open(map);
      }
    }

    pathRefs.current.forEach(polyline => {
      polyline.setMap(null);
    });
    pathRefs.current = [];

    const segments = groupCoordinatesByDistance(pathCoordinates, 4);

    segments.forEach(segment => {
      if (segment.length >= 2) {
        const polyline = new google.maps.Polyline({
          path: segment,
          geodesic: true,
          strokeColor: '#288dff',
          strokeOpacity: 1,
          strokeWeight: 4,
          map,
        });
        pathRefs.current.push(polyline);
      } else if (segment.length === 1) {
        const polyline = new google.maps.Polyline({
          path: [segment[0], segment[0]],
          geodesic: true,
          strokeColor: '#288dff',
          strokeOpacity: 1,
          strokeWeight: 4,
          map,
        });
        pathRefs.current.push(polyline);
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      pathRefs.current.forEach(polyline => {
        polyline.setMap(null);
      });
      pathRefs.current = [];
      if (tooltipRef.current) {
        tooltipRef.current.close();
        tooltipRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, deviceHistory, selectedHistoryPoint, timezone]);

  return null;
}
